import {computed, Inject, Injectable, Optional} from '@angular/core';
import {Observable, of} from 'rxjs';
import {DashboardApi} from '../api/dashboard.api';
import {DashboardState} from '../state/dashboard.state';
import {
  ChartInteractiveSort,
  ChartModel,
  ColumnData,
  DescriptorColumn,
  LegendColumn,
  NumberDisplayType,
  ValueColumn,
} from '../models/chart';
import {catchError, map, retry, take, tap} from 'rxjs/operators';
import {ChartType} from '../models/chartType';
import {GridItem} from '../models/gridItem';
import {DashboardAndGroupInList} from '../models/dashboard-and-group-in-list';
import {ColoringSetService, ColorSet} from '../services/coloring-set.service';
import {ToastrWrapperService} from '../../shared/services/toastr-wrapper.service';
import {Datasource} from '../models/datasource';
import {Filter} from '../models/filter';
import {FilterType} from '../models/filter-type.enum';
import {Submit} from '../../shared/models/submit.enum';
import {Dashboard, DashboardGrid, EditDashboardTypeEnum} from '../models/dashboard';
import {DataModuleType} from '../models/data-module-type.type';
import {DataType} from '../models/data-type.enum';
import {DashboardColumn} from '../models/dashboard-column';
import {CommentType} from '../models/comment.type';
import {Comment} from '../models/comment.model';
import {CommentState} from '../models/comment-state.type';
import {UserService} from '../../shared/services/user.service';
import {SubmitType} from '../../shared/models/submit.type';
import {ColumnType} from '../models/columnType.model';
import {CalculateColumnModeType} from '../models/calculateColumnModeType';
import {CalculateColumnModel} from '../../data-source/models/calculate-column.model';
import {ParameterProperties} from '../models/parameterProperties.model';
import {ChartDesignVersionManagerService} from '../services/chart-design-version-manager.service';
import {GridVersionManagerService} from '../services/grid-version-manager.service';
import {Controller} from '../models/controller.model';
import {ColumnColor} from '../models/columnColor.model';
import {MapModel} from '../models/map.model';
import {InteractionValues} from '../models/interaction-values.model';
import {ShareProperties} from '../models/share-properties.model';
import {Router} from '@angular/router';
import {UserFilterService} from '../../shared/services/user-filter.service';
import {AbstractFacade} from '../../shared/models/abstract-facade';
import {UserFilter} from '../../shared/models/user-filter.model';
import {UserFilterDetails} from '../../shared/models/userFilterDetails.model';
import {UserInUserFilter} from '../../shared/models/userInUserfilter.model';
import {ConfigFormatOptionService} from '../services/config-format-option.service';
import {FormatServiceInterface} from '../services/interfaces/format-service.interface';
import {JsonAdapterService} from '../../shared/services/json-adapter.service';
import {DashboardDesignSettingsVersionManagerService} from '../services/dashboard-design-settings-version-manager.service';
import {ChartDesignSettings} from '../models/chartDesignSettings';
import {DashboardDesignSetting} from '../models/dashboard-design-setting';
import {PermissionServiceInterface} from '../../shared/models/permisision-service.interface';
import {ChartFactoryService} from '../services/chart-factory.service';
import {DashboardControllersFilter} from '../models/dashboard-controllers-filter';
import {ExportChartDataType} from '../models/exportChartData.type';
import {ControllerValue} from '../models/controller-value.model';
import {TemporaryDashboard} from '../models/temporary-dashboard.model';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {SaveDashboardConfirmationComponent,} from '../components/dialogs/save-dashboard-confirmation/save-dashboard-confirmation.component';
import {DashboardType} from '../models/dashboard.type';
import {SidebarService} from '../../shared/services/sidebar.service';
import {SortOrder} from '../../shared/models/sort.order';
import {ChartAnnotationSetting} from '../models/chart-annotation-setting.model';
import { SortError } from '../models/sort-error.enum';
import {GridItemFactoryService} from "../services/grid-item-factory.service";
import { DashboardInfoDto } from '../models/dahsbaord-id.model';
import { MessageDialogComponent } from '../../shared/components/message-dialog/message-dialog.component';
import { environment } from '../../../../environments/environment';
import * as _ from 'lodash'
import { TruncateHtmlPipe } from '../../shared/pipes/truncate-html/truncate-html.pipe';
import { ExportWarningModalComponent } from '../components/dialogs/export-warning-modal/export-warning-modal.component';
import { ImportDashboardDatasourcesModalComponent } from '../components/dialogs/import-dashboard-datasources-modal/import-dashboard-datasources-modal.component';

@Injectable()
export class DashboardFacade extends AbstractFacade{
  chartsToLoadList:any = []
  chartsToLoadInParallelModeList:any = []
  chartsToLoadListIndex:any = 0
  allWorkspaceId = environment.allWorkspaceId;
  baseAddress = environment.api + '/File/Download?path=';
  constructor(
    @Optional() private dashboardApi: DashboardApi,
    @Optional() private dashboardState: DashboardState,
    @Optional() private toastr: ToastrWrapperService,
    @Optional() private colorService: ColoringSetService,
    @Optional() private userService: UserService,
    @Optional() private chartDesignVersionManager: ChartDesignVersionManagerService,
    @Optional() private gridVersionManager: GridVersionManagerService,
    @Optional() private configFormatOptionService: ConfigFormatOptionService,
    @Optional() private userFilterService: UserFilterService,
    @Optional() private chartFactoryService: ChartFactoryService,
    @Optional() private jsonAdapterService: JsonAdapterService,
    @Optional() private dashboardDesignSettingsVersionManagerService: DashboardDesignSettingsVersionManagerService,
    @Optional() private sidebarService: SidebarService,
    @Optional() private router: Router,
    @Optional() private dialogService: NbDialogService,
    @Optional() @Inject('Format') private formattingService: FormatServiceInterface,
    @Optional() @Inject('DashboardPermission') private dashboardPermissionService: PermissionServiceInterface<Dashboard>,
    @Optional() @Inject('ChartPermission') private chartPermissionService: PermissionServiceInterface<Partial<ChartModel>>,
  ) {
      super();
  }

  readonly state = computed(() => ({
    isCopyChartInProgress: this.dashboardState.isCopyChartInProgress(),
    isAddNewChartInProgress: this.dashboardState.isAddNewChartInProgress(),
    historyCurrent: this.dashboardState.dashboardHistoryCurrent(),
    historyList: this.dashboardState.dashboardHistoryList(),
    isDashboardHistoryLoading: this.dashboardState.dashboardHistoryLoading(),
    recoveryCurrent: this.dashboardState.dashboardRecoveryCurrent(),
    isDashboardRecoveryLoading: this.dashboardState.dashboardRecoveryLoading(),
    isDashboardRecoverySuccess: this.dashboardState.dashboardRecoverySuccess(),
    pinCurrent: this.dashboardState.dashboardPinCurrent(),
    isDashboardPinLoading: this.dashboardState.dashboardPinLoading(),
    hasPinnedHistory: (this.dashboardState.dashboardHistoryList() || []).some((x) => x.isPinHistory),
    isDashboardInHistoryMode: this.dashboardState.isDashboardInHistoryMode(),
    dashboardNameDuplicated: this.dashboardState.dashboardNameDuplicated(),
    chartNameDuplicated: this.dashboardState.chartNameDuplicated(),
    nameSubmitState: this.dashboardState.nameSubmitState(),
    paintFormatData: this.dashboardState.paintFormatState(),
    // --- Import / Export ---
    importDashboardId: this.dashboardState.importDashboardId(),
    importDatasources: this.dashboardState.importDatasources(),
    importStepIndex: this.dashboardState.importStepIndex(),
    importCurrentDatasource: this.dashboardState.importCurrentDatasource(),
    importTotalSteps: this.dashboardState.importTotalSteps(),
    importIsLastStep: this.dashboardState.importIsLastStep(),
    importLoading: this.dashboardState.importLoading(),
    importFinalizeLoading: this.dashboardState.importFinalizeLoading(),
    exportDashboardLoading: this.dashboardState.exportDashboardLoading(),
    //Advanced Card Grid
    advancedCardGridData: this.dashboardState.advancedCardGridState(),

  }));
  private importDialogRef: NbDialogRef<ImportDashboardDatasourcesModalComponent> | null = null;

  chartDatasourceState = (chartId: string) =>
    computed(() =>
      this.dashboardState.defaultDatasourceByChart()[chartId] ?? {
        list: [],
        selectedId: null,
        hasDefault: false,
        loading: false,
        tickTime: null
      }
    );

  //edit mode
  editMode$(): Observable<boolean> {
    return this.dashboardState.isEditMode$();
  }

  isDashboardInEditMode():boolean{
    return this.dashboardState.getIsEditModeValue()
  }

  getDashboardValue(){
    return this.dashboardState.getDashboardValue()
  }

  toggleEditMode(workspaceId: string, dashboardId: string, editModeType:EditDashboardTypeEnum, tempDashboardId?: string): void {
    const currentIsEditMode = this.dashboardState.getIsEditModeValue();
    if(currentIsEditMode === false) {
        this.dashboardState.resetDashboardState();
        this.dashboardState.toggleEditMode();
        if(editModeType === EditDashboardTypeEnum.PUBLISH){
          this.loadTemporaryDashboard(workspaceId, dashboardId);
        }else{
          setTimeout(()=>{
            this.dashboardState.setTemporaryDashboard({...this.dashboardState.getTemporaryDashboardValue()});
            this.dashboardState.setDashboardTypeState('Edit');
          }, 200)
        }
        this.dashboardState.clearChartsUserSort();
        this.dashboardState.getChartsHasUserSortValue().forEach(chart => {
            this.loadChartData(this.dashboardState.workspaceId,this.dashboardState.dashboardId,chart);
        });
        this.dashboardState.resetChartsHasUserSort();
    } else {
      this.updateShouldSaveDashboard();
      this.saveDashboardConfirmation(true);
    }
  }

  setEditMode(isEditMode: boolean): void {
      this.dashboardState.setEditMode(isEditMode);
  }

  //dashboard
  getDashboard$(): Observable<any> {
    return this.dashboardState.getDashboard$();
  }
  dashboardIsLoading$(): Observable<boolean>{
    return this.dashboardState.dashboardIsUpdating$();
  }

  getDashboardIds(){
    return {
      mainDashboardId: this.dashboardState.getMainDashboardId(),
      dashboardId: this.dashboardState.dashboardId
    }
  }
  getDashboardIdAndWorkspace(){
    return {
      workspaceId: this.dashboardState.workspaceId,
      dashboardId: this.dashboardState.dashboardId,
    }
  }
  getDashboardName(): string {
    return this.dashboardState.getDashboardValue()?.name ?? '';
  }
  loadDashboard(
  workspaceId: string,
  dashboardId: string,
  needToLoadShareProperties: boolean = false,
  isAutoRefresh: boolean = false,
  cb?: any,
  isHistory: boolean = false
) {
  this.dashboardState.dashboardSetUpdating(true);
  const apiParams: any = { workspaceId, dashboardId };
  if (isHistory) {
    apiParams.isHistory = true;
  }

  this.dashboardApi.getDashboard(apiParams).pipe(retry(1)).subscribe(
    (response) => {
      let dashboard: Dashboard = response.data;
      this.dashboardState.checkDrillThroughStates();
      this.loadDashboardDrillThroughFilters(workspaceId, dashboardId);
      dashboard = this.updateDashboardGridVersion(dashboard);
      const emptyGridResult = DashboardFacade.fixEmptyGridIssue(dashboard);
      if (emptyGridResult) {
        dashboard.chartsGrid = emptyGridResult;
      }
      dashboard = this.updateDashboardDesignSettingsVersion(dashboard);
      dashboard = this.dashboardPermissionService.addPermissionToEntity(
        dashboard,
        dashboard.permissions,
        this.userService.getUserMinInfoValue(),
        false
      );

      const hasAccessToShareProperties =
        needToLoadShareProperties && dashboard.permissionsResponseMap.get('Share')?.isChecked;
      if (hasAccessToShareProperties) {
        this.loadShareProperties(dashboard);
      }
      this.loadChartsPermissions(dashboard);

      if (isAutoRefresh && dashboard?.dashboardDesignSettings?.autoRefreshInterval) {
        const interval = dashboard.dashboardDesignSettings.autoRefreshInterval;
        let intervalText = '';
        if (interval < 60) {
          intervalText = `${interval} دقیقه یک‌بار`;
        } else {
          const hours = Math.floor(interval / 60);
          intervalText = `${hours} ساعت یک‌بار`;
        }
        this.toastr.showInfo(
          `به‌روزرسانی خودکار فعال است. داشبورد هر ${intervalText} بازخوانی می‌شود.`
        );
      }
     this.applyDashboardDisplayName(dashboard)
      if (cb) {
        cb();
      }
    },
    (errorRes) => {
      if (errorRes?.status === 404) {
        this.router.navigate([`/home/${this.allWorkspaceId}`])
        return;
      }
      this.toastr.showError(errorRes.error.message);
    }
  );
}

  updateDashboardChartGridsState(dashboard: Dashboard): void {
      this.updateShouldSaveDashboard();
      this.dashboardState.updateDashboard(dashboard);
      this.dashboardState.hasChangedChartGrids = true;
  }

  updateDashboardChartGrids(): void {
      if(this.dashboardState.hasChangedChartGrids) {
          this.updateDashboard(this.dashboardState.getDashboardValue(), false);
      }
  }

  updateDashboard(dashboard: Dashboard, isRenameDashboard: boolean = false, disableSaveAlertDialog: boolean=false, cb?:any, payload?:any, disableBody?:any) {
    return this.dashboardApi.updateDashboard(this.dashboardState.workspaceId, this.dashboardState.dashboardId, payload || dashboard).subscribe(
      () => {
        if(!disableBody){
          if(!disableSaveAlertDialog){
            this.updateShouldSaveDashboard();
          }
          this.dashboardState.updateDashboard(dashboard);
          if(isRenameDashboard) {
          }
          this.dashboardState.hasChangedChartGrids = false;
        }
        if(cb){
          cb()
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.dashboardState.hasChangedChartGrids = false;
        if(cb){
          cb()
        }
      },
    );
  }

  updateDashboardName(dashboard: Dashboard) {
    this.dashboardState.setNameSubmitState('submitting');
    this.setDashboardChartNameSubmitState('submitting');
    this.dashboardApi.updateDashboardName(dashboard).subscribe(
      (data) => {
          if (data.data.statusCode === 'ObjectAlreadyExists') {
            this.dashboardState.setDashboardNameDuplicated(true);
            this.setNameSubmitState('duplicateName');
            this.setDashboardChartNameSubmitState('duplicateName');
            // dashboard.dashboardDesignSettings.dashboardRichName = data.data.richName
          } else {
            this.updateShouldSaveDashboard();
            this.dashboardState.updateDashboard(dashboard);
            this.dashboardState.updateDashboardNameInList(dashboard);
              this.dashboardState.hasChangedChartGrids = false;
            this.dashboardState.setDashboardNameDuplicated(false);
            this.setDashboardChartNameSubmitState('submitted');
            this.dashboardState.setNameSubmitState('submitted');
          }

      },
      (errorRes) => {

          this.dashboardState.hasChangedChartGrids = false;
          this.dashboardState.setDashboardNameDuplicated(false);
          this.toastr.showError(errorRes.error.message);
          this.dashboardState.setNameSubmitState('notSubmitted');
          this.setDashboardChartNameSubmitState('notSubmitted');
      }
    );
  }
    //important comment for this method:
    // This method is responsible for resolving and synchronizing the differences between
    // `name`, `richName`, `pureName`, and `dashboardRichName` inside the dashboard design settings.
    private applyDashboardDisplayName(dashboard: Dashboard): void {
      let extraText : string
      let displayName = dashboard.dashboardDesignSettings?.dashboardRichName || "";
      const isRichName = displayName.slice(0, 2) === "<p";
      // This branch handles cases where the dashboard name was duplicated and the API did not
      // persist the updated name correctly. As a result, the name returned by `getDashboard`
      // differs from the value stored in `dashboardRichName`. In this scenario, the rich name
      // is trusted and rendered directly without further processing.

      if(dashboard.richName && TruncateHtmlPipe.truncateText(dashboard.richName) !== dashboard.name ){
        dashboard.dashboardDesignSettings.dashboardRichName = dashboard.richName
        return;
      }

      const truncateRichName = TruncateHtmlPipe.truncateText(
        dashboard.dashboardDesignSettings?.dashboardRichName || ""
      );
      // The next scenario covers cases where a comparison is required. Any extra text provided
      // by the API (relative to the truncated or designed dashboard title) is detected and
      // appended to the displayed name using the default title font and styling.

      if (isRichName) {
        displayName = displayName.slice(3, displayName.length - 4);
      if(dashboard.richName){
        const pureRichName = TruncateHtmlPipe.truncateText(
          dashboard.richName
        );
        extraText = pureRichName?.slice(truncateRichName.length) || "";
        }else{
          extraText = dashboard.name?.slice(truncateRichName.length) || "";
        }
        if (extraText && extraText.trim()) {
          const styledExtra = `
            <span style="color:#4d4d4d;font-size:24px;font-family:iranyekan;">
              ${extraText}
            </span>`;
          displayName += " " + styledExtra;
        }
        dashboard.dashboardDesignSettings.dashboardRichName = `<p>${displayName}</p>`;
        dashboard.richName = `<p>${displayName}</p>`;

        const newTruncateRichName = TruncateHtmlPipe.truncateText(
          displayName
        );

        if (newTruncateRichName !== dashboard.name) {
          dashboard.dashboardDesignSettings.dashboardRichName =
          dashboard.richName ? `<span style="color:#4d4d4d;font-size:24px;font-family:iranyekan;">
              ${dashboard.richName}
            </span>` :`<p> <span style="color:#4d4d4d;font-size:24px;font-family:iranyekan;">
              ${dashboard.name}
            </span></p>`;

            dashboard.richName = `
            <p> <span style="color:#4d4d4d;font-size:24px;font-family:iranyekan;">
              ${dashboard.name}
            </span></p>`;
        }
    }
    // The final fallback handles cases where `dashboardRichName` does not exist at all.
    // This typically means the dashboard title has never been edited. In this case, the
    // display name is derived directly from `dashboard.name` and rendered using the
    // default dashboard title style.
    else {
            dashboard.dashboardDesignSettings.dashboardRichName =
            `<p> <span style="color:#4d4d4d;font-size:24px;font-family:iranyekan;">
                ${dashboard.name}
              </span></p>`;
            dashboard.richName = `
            <p> <span style="font-size:24px;color:#4D4D4D;;font-family:iranyekan;">
              ${dashboard.name}
            </span></p>`;
        }
  }




  updateChartName(chart: Partial<ChartModel>, value?): void {
    this.dashboardApi.updateChartName(chart, this.dashboardState.workspaceId, this.dashboardState.dashboardId, value).subscribe(
      (data) => {
        this.dashboardState.setNameSubmitState('submitting');
        // this.setDashboardChartNameSubmitState('submitting');
        if(data.data.statusCode === 'ObjectAlreadyExists') {
          this.dashboardState.setChartNameDuplicated(true);
          this.dashboardState.setNameSubmitState('duplicateName');
          // this.setDashboardChartNameSubmitState('duplicateName');
        } else {
          this.updateShouldSaveDashboard();
          this.dashboardState.setChartNameDuplicated(false);
        this.dashboardState.setNameSubmitState('submitted');
        // this.setDashboardChartNameSubmitState('submitted');

        }
        const updatedChart = {
          ...chart,
          name: data.data.name
        }
        this.dashboardState.updateChartName(updatedChart);
      },
      (errorRes) => {
        this.dashboardState.setNameSubmitState('submitted');
        // this.setDashboardChartNameSubmitState('submitted');
            this.dashboardState.setChartNameDuplicated(false);
            this.toastr.showError(errorRes.error.message);
          }
      );
   }


  setChartNameDuplicated(value: boolean) {
      this.dashboardState.setChartNameDuplicated(value);
  }

  updateDashboardChartsNumberDisplayTypes(dashboard: Dashboard): void {
    const charts = this.dashboardState.updateDashboardChartsNumberDisplayTypes(dashboard.charts,
        dashboard.dashboardDesignSettings.numberDisplayType);
    charts.forEach((chart, index, self) => {
        if(chart?.drillingProperties?.drillLevel > 1) {
            chart.drillingProperties.drillDownRealColumns.forEach((column, columnIndex) => {
                chart.drillingProperties.drillDownShowColumns[columnIndex] =
                    this.changeColumnDisplayingNumbers(chart.numberDisplayType ,column);
            });
            self[index] = chart;
        }
    });
    this.dashboardState.updateCharts(charts);
  }

  changeColumnDisplayingNumbers(numberDisplayType: NumberDisplayType, value: string | number): string {
      return this.formattingService.formatData(value, this.configFormatOptionService.configFormatOption(undefined,
          numberDisplayType, undefined, undefined));
  }

  loadShareProperties(dashboard: Dashboard): void {
      this.dashboardApi.getShareProperties(dashboard.workspaceId,dashboard.dashboardId).subscribe(
          (res) => {
              this.dashboardState.updateDashboardShareProperties(res.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  updateDashboardShareLinkActivityStatus(isEnabled: boolean): void {
      this.dashboardApi.setShareLinkActivityStatus(isEnabled, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId).subscribe(() => {
              this.dashboardState.updateDashboardShareLinkActivityStatus(isEnabled);
              },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          });
  }

  getDashboardShareProperties$(): Observable<ShareProperties> {
      return this.dashboardState.getDashboardShareProperties$();
  }
  // save dashboard
  setDashboardTypeState(dashboardType: DashboardType): void {
      this.dashboardState.setDashboardTypeState(dashboardType);
  }

  getDashboardTypeState$(): Observable<DashboardType> {
      return this.dashboardState.getDashboardTypeState$();
  }

  loadTemporaryDashboard(workspaceId: string, dashboardId: string): void {
      this.dashboardApi.editDashboard(workspaceId, dashboardId).subscribe(
          (res) => {
              this.dashboardState.setTemporaryDashboard(res.data);
              this.dashboardState.setDashboardTypeState('Edit');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  checkEditDashboard(workspaceId: string, dashboardId: string){
    return this.dashboardApi.checkEditDashboard(this.dashboardState.workspaceId, this.dashboardState.dashboardId)
  }

  setTemporaryDashboard(temporaryDashboard: TemporaryDashboard): void {
      this.dashboardState.setTemporaryDashboard(temporaryDashboard);
  }

  getTemporaryDashboard$(): Observable<TemporaryDashboard> {
      return this.dashboardState.getTemporaryDashboard$();
  }

  setShouldSaveDashboard(shouldSave: boolean): void {
      this.dashboardState.setShouldSaveDashboard(shouldSave);
  }

  updateShouldSaveDashboard(): void {
      this.dashboardState.updateShouldSaveDashboard();
  }

  getShouldSaveDashboard$(): Observable<any> {
      return this.dashboardState.getShouldSaveDashboard$();
  }

  saveDashboardConfirmation(shouldDeleteTemporaryDashboard: boolean): void {
      if(this.dashboardState.getShouldSaveDashboardValue()) {
          this.dialogService.open(SaveDashboardConfirmationComponent, {
              closeOnBackdropClick: false,
              context: {
                  title: 'ذخیره تغییرات',
                  temporaryDashboard: this.dashboardState.getTemporaryDashboardValue(),
                  shouldDeleteTemporaryDashboard,
                  dashboardName: this.dashboardState.getDashboardValue().name,
              },
          }).onClose.subscribe((state) => {
              if(state === 'deleteTempDashboard') {
                  this.deleteTemporaryDashboard(this.dashboardState.getTemporaryDashboardValue());
                  this.changeStatesForDeletingTemporaryDashboard('View');
              }
          });
      } else if (shouldDeleteTemporaryDashboard) {
          this.deleteTemporaryDashboard(this.dashboardState.getTemporaryDashboardValue());
          this.changeStatesForDeletingTemporaryDashboard('View');
      }
  }

  changeStatesForDeletingTemporaryDashboard(dashboardType: DashboardType = 'None'): void {
      this.dashboardState.toggleEditMode();
      this.dashboardState.setDashboardTypeState(dashboardType);
      this.dashboardState.setTemporaryDashboard(null);
      this.dashboardState.setShouldSaveDashboard(false);
  }

  saveDashboard(temporaryDashboard: TemporaryDashboard, submitType: SubmitType): void {
      this.setSubmitState('submitting');
      this.dashboardApi.saveDashboard(temporaryDashboard).subscribe(
          () => {
              this.setSubmitState(submitType);
              // this.dashboardState.setShouldSaveDashboard(false);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.dashboardState.setShouldSaveDashboard(false);
              this.setSubmitState('submittedWithError');
          },
      );
  }

  //TODO: remove this function from code( not needed anymore)
  deleteTemporaryDashboard(temporaryDashboard: TemporaryDashboard): void {
      this.dashboardState.setTemporaryDashboard(null);
      this.setSubmitState('submitted');
      // this.setSubmitState('submitting');
      // this.dashboardApi.deleteDashboard(temporaryDashboard.workspaceId, temporaryDashboard.tempDashboardId).subscribe(
      //     () => {
      //         this.dashboardState.setTemporaryDashboard(null);
      //         this.setSubmitState('submitted');
      //     },
      //     (errorRes) => {
      //         this.toastr.showError(errorRes.error.message);
      //         this.setSubmitState('submittedWithError');
      //     },
      // );
  }

  //parameters
  getParametersOfDashboard$(): Observable<Partial<Datasource>[]> {
    return  this.dashboardState.getParametersOfDashboard$();
  }

  updateParametersOfDashboard(parameters: Partial<Datasource>[]): void {
    this.dashboardState.updateParametersOfDashboard(parameters);
  }

  createParameter(parameterProperties: ParameterProperties) {
    this.dashboardApi.createParameter(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, parameterProperties).subscribe(
      (res) => {
        this.updateShouldSaveDashboard();
        this.dashboardState.addParametersToDashboard(res.data);
        this.updateParameterSubmit('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.updateParameterSubmit('submittedWithError');
      },
    );
  }

  deleteParameter( dataSourceId: string) {
    this.dashboardApi.deleteParameter(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, dataSourceId).subscribe(
    () => {
      this.updateShouldSaveDashboard();
      this.dashboardState.deleteParameter(dataSourceId);
      this.dashboardState.setSelectedChartMissingFilter(dataSourceId);
        const dashboard = {...this.dashboardState.getDashboardValue()};
        dashboard.charts = this.dashboardState.setMissingChartsColumns(dashboard.charts, dataSourceId);
        this.dashboardState.updateDashboard(dashboard);
    },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
    },
    );
  }

  updateParameter(parameter: Partial<Datasource>) {
    this.dashboardApi.updateParameter(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,parameter.dataSourceId, parameter.parameterProperties).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.reloadAllDashboardCharts();
        this.dashboardState.updateSingleParameter(parameter);
        this.updateParameterSubmit('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.updateParameterSubmit('submittedWithError');
      },
    );
  }

  updateParameterSubmit(string: SubmitType): void{
     this.dashboardState.updateParameterSubmit(string);
  }

  getParameterSubmit$(): Observable<SubmitType> {
    return  this.dashboardState.getParameterSubmit$();
  }

  //data sources
  loadDataSourcesOfDashboard(workspaceId: string, dashboardId: string): void{
    this.dashboardApi.getDataSourcesOfDashboard(workspaceId, dashboardId).subscribe(
      (res) => {
        this.dashboardState.updateDataSourcesOfDashboard(res.data);
        this.loadDashboardFilters(workspaceId, dashboardId);
        this.loadDefaultDataSource(workspaceId, dashboardId)
        this.refreshSelectedChartFiltersMissingState();
        this.dashboardState.updateDataSourcesLoaded(true);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteDataSourceFromDashboard(dataSourceId: string): void {
    this.dashboardApi.deleteDataSourceFromDashboard(this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, dataSourceId).subscribe(
      () => {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteDataSourceFromDashboard(dataSourceId);
        this.loadDataSourcesOfDashboard(this.dashboardState.workspaceId, this.dashboardState.dashboardId);
        this.dashboardState.setSelectedChartMissingFilter(dataSourceId);
        const dashboard = {...this.dashboardState.getDashboardValue()};
        dashboard.charts = this.dashboardState.setMissingChartsColumns(dashboard.charts, dataSourceId);
        this.dashboardState.updateDashboard(dashboard);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteDataSourceColumnFromDashboard(dataSourceId: string, columnId: string): void {
    this.dashboardApi.deleteDataSourceColumnFromDashboard(this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, dataSourceId, columnId).subscribe(
      () => {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteDataSourceColumnFromDashboard(dataSourceId, columnId);
        this.dashboardState.setSelectedChartMissingFilter(dataSourceId, columnId);
        const dashboard = {...this.dashboardState.getDashboardValue()};
        dashboard.charts = this.dashboardState.setMissingChartsColumns(dashboard.charts, dataSourceId, columnId);
        this.dashboardState.updateDashboard(dashboard);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  setCustomNameForDataSourceColumnInDashboard(dataSourceId: string, columnId: string, customName: string): void {
    this.dashboardApi.setCustomNameForColumn(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        dataSourceId, columnId, customName).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.setCustomNameForDataSourceColumnInDashboard(columnId, customName, dataSourceId);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getDataSourcesOfDashboard$(): Observable<Partial<Datasource>[]> {
    return this.dashboardState.getDataSourcesOfDashboard$();
  }

  loadSelectedFilterColumnValues(filter: Partial<Filter>, appendData: boolean = false): void {
      this.dashboardApi.getColumnValues(filter,this.dashboardState.workspaceId, this.dashboardState.dashboardId).subscribe(
          (values) => {
              this.dashboardState.updateFilterColumnValues(values.data.data, filter.filterId, appendData, filter.paginationValues.searchKey);
          },
      );
  }

  getSelectedFilterColumnValues$(): Observable<any> {
    return this.dashboardState.getSelectedFilterColumnValues$();
  }

  getColumnDataModuleType(columnId: string, dataSourceId: string): DataModuleType {
    return this.dashboardState.getColumnDataModuleType(columnId, dataSourceId);
  }

  getColumnDataType(columnId: string, dataSourceId: string): DataType {
    return this.dashboardState.getColumnDataType(columnId, dataSourceId);
  }

  getDataSourcesLoaded$(): Observable<boolean> {
    return this.dashboardState.getDataSourcesLoaded$();
  }

  updateDescriptorColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateDescriptorColumn(newColumn, this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        chart.chartId).subscribe(
      (res) => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateDescriptorColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateValueColumn(newColumn: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateValueColumn(newColumn, this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        chart.chartId).subscribe(
      (res) => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateValueColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        if ((chart.chartType === 'KPIChart' && res.data?.hasWarning)) {
          this.toastr.showWarning(res.data.warningMessage);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateSecondValueColumn(newColumn: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    if(chart.chartType === 'BubbleChart'){
      newColumn.columnAggregationType = 'NoAggregation';
    }
    this.dashboardApi.updateSecondValueColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateSecondValueColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        if ((chart.chartType === 'KPIChart' && res.data?.hasWarning)) {
          this.toastr.showWarning(res.data.warningMessage);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }



  changeDescriptorColumnPriority(descriptorColumn: DescriptorColumn, newPriority: number, chart: Partial<ChartModel>,
                                 previousIndex: number,currentIndex: number): void {
      this.dashboardApi.changeDescriptorColumnPriority(descriptorColumn, newPriority,
          this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart).subscribe(
            (res: any) => {
              const status: SortError = res?.data?.priorityStatus;
              switch (status) {
                case SortError.NumberOfColumnsNotEqual:
                  this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
                  break;
                case SortError.PrioritiesNotEqual:
                  this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
                  break;
              }
              this.updateShouldSaveDashboard();
              this.dashboardState.changeDescriptorColumnPriority(descriptorColumn, newPriority, currentIndex,
                  previousIndex, chart.chartId);
              this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
            },
            (errorRes) => {
                this.toastr.showError(errorRes.error.message);
            },
      );
  }

  changeValueColumnPriority(valueColumn: ValueColumn, newPriority: number, chart: Partial<ChartModel>,
                            previousIndex: number,currentIndex: number): void{
    this.dashboardApi.changeValueColumnPriority(valueColumn, newPriority,
        this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.changeValueColumnPriority(valueColumn, newPriority, currentIndex,
          previousIndex, chart.chartId);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  changeLegendValueColumnPriority(valueColumn: ValueColumn, newPriority: number, chart: Partial<ChartModel>,
                            previousIndex: number,currentIndex: number): void{
    this.dashboardApi.changeLegendValueColumnPriority(valueColumn, newPriority,
      this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.changeLegendValueColumnPriority(valueColumn, newPriority, currentIndex,
          previousIndex, chart.chartId);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  changeSecondValueColumnPriority(valueColumn: ValueColumn, newPriority: number, chart: Partial<ChartModel>,
                                  previousIndex: number,currentIndex: number): void {
    this.dashboardApi.changeSecondValueColumnPriority(valueColumn,newPriority,
        this.dashboardState.workspaceId, this.dashboardState.dashboardId,chart).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.changeSecondValueColumnPriority(valueColumn, newPriority, currentIndex,
          previousIndex, chart.chartId);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }


  updateLegendColumn(newColumn: Partial<LegendColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateLegendColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateLegendColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateControllerColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.updateControllerColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateDescriptorColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteDescriptorColumn(column: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteDescriptorColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
          (res: any) => {
            const status: SortError = res?.data?.priorityStatus;
            switch (status) {
              case SortError.NumberOfColumnsNotEqual:
                this.toastr.showInfo('مرتب‌سازی غیرفعال شد: تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
                break;
              case SortError.PrioritiesNotEqual:
                this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
                break;
            }
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteDescriptorColumn(chart.chartId, column.id);
        if (this.shouldLoadChartDataForDescriptorColumn(chart)) {
            this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        } else {
            this.dashboardState.updateChartData([], chart.chartId);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteLegendColumn(column: Partial<LegendColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteLegendColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteLegendColumn(chart.chartId, column.id);
          if(this.shouldLoadChartDataForLegendColumn(chart)) {
              this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
          } else {
              this.dashboardState.updateChartData([], chart.chartId);
          }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteValueColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteValueColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
          (res: any) => {
            const status: SortError = res?.data?.priorityStatus;
            switch (status) {
              case SortError.NumberOfColumnsNotEqual:
                this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
                break;
              case SortError.PrioritiesNotEqual:
                this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
                break;
            }
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteValueColumn(chart.chartId, column.id);
        if (this.shouldLoadChartDataForValueColumn(chart)) {
          this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        } else {
          this.dashboardState.updateChartData([], chart.chartId);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteSecondValueColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteSecondValueColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
          (res: any) => {
            const status: SortError = res?.data?.priorityStatus;
            switch (status) {
              case SortError.NumberOfColumnsNotEqual:
                this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
                break;
              case SortError.PrioritiesNotEqual:
                this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
                break;
            }
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteSecondValueColumn(chart.chartId, column.id);
        if (chart.chartType === 'KPIChart' && (chart.chartDataSettings.valueColumns.length === 0 ||
            chart.chartDataSettings.descriptorColumns.length === 0)) {
          this.dashboardState.updateChartData([], chart.chartId);
        } else {
          this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteSortColumn(column: Partial<ChartInteractiveSort>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteSortColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
          (res: any) => {
            const status: SortError = res?.data?.priorityStatus;
            switch (status) {
              case SortError.NumberOfColumnsNotEqual:
                this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
                break;
              case SortError.PrioritiesNotEqual:
                this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
                break;
            }
            this.updateShouldSaveDashboard();
            this.dashboardState.deleteSortColumn(chart.chartId, column.id);
            this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        },
        (errorRes) => {
          chart.isLoading = false;
            this.toastr.showError(errorRes.error.message);
        },
    );
}
  deleteControllerColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteControllerColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateChartData([], chart.chartId);
        this.dashboardState.deleteDescriptorColumn(chart.chartId, column.id);
        this.deleteControllerValue(chart.chartId);
        if ((chart.chartType === 'DateRangeController' ||
          chart.chartType === 'DropdownController' || chart.chartType === 'DateListController') &&
          chart.defaultValuesForController.defaultValues.length > 0) {
          chart.defaultValuesForController.defaultValues = [];
          if(chart.defaultValuesForController.defaultValueType === 'AdvancedSingle' ||
              chart.defaultValuesForController.defaultValueType === 'AdvancedRange') {
              chart.defaultValuesForController.defaultValueAdvancedSingle = null;
              chart.defaultValuesForController.defaultValueAdvancedRange = null;
          }
          if(chart.defaultValuesForController.defaultValueType === "Relative"){
            chart.defaultValuesForController.defaultValueRelative = {
              parameterDate: "TodayDate",
              parameterDateValue:null,
              scope: "Day",
              scopeValue: null,
              timeMode: "Past",
            };
          }
          if (chart.chartType === 'DateListController') {
            chart.defaultValuesForController.defaultValueDateList = {
              calenderType: 'None',
              defaultValueDateList: undefined,
            };
          }
          this.dashboardState.updateDefaultControllerValues(chart);
          this.dashboardState.updateOneChart(chart);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  /**
   * @param column ?
   * @param chart ?
   * @param columnType ?
   * @param index ?
   * this method is called when a new descriptor column is added
   * ( aka : mehvar mabna drop event )
   */
  addDescriptorColumn(column: Partial<DescriptorColumn>, chart: Partial<ChartModel>,
                      columnType: ColumnType, index: number): void {
    if(chart.chartType === 'GaugeChart' || chart.chartType === 'AdvancedGaugeChart') {
      column.columnAggregationType = column.dataModuleType === 'Quantity' ? 'Sum' : 'Count';
    }
    chart.isLoading = true;
    this.dashboardApi.addDescriptorColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
          (res: any) => {
            const status: SortError = res?.data?.priorityStatus;
            switch (status) {
              case SortError.NumberOfColumnsNotEqual:
                this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
                break;
              case SortError.PrioritiesNotEqual:
                this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
                break;
            }
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addDescriptorColumn(column, chart.chartId,index);
        if (this.shouldLoadChartDataForDescriptorColumn(chart)) {
          this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        } else {
          chart.isLoading = false;
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateMapFileDescriptorColumn(column: Partial<DescriptorColumn>, chart: Partial<ChartModel>, mapId: string): void {
    chart.isLoading = true;
    this.dashboardApi.updateMapFileDescriptorColumn(column, this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, chart.chartId, mapId).subscribe((res: any) => {
        this.updateShouldSaveDashboard();
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        chart.isLoading = false;
      }, (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  shouldLoadChartDataForDescriptorColumn(chart: Partial<ChartModel>): boolean {
      const hasData = chart.chartType !== 'SankeyChart' && chart.chartType !== 'KPIChart' &&
          chart.chartType !== 'CollapsableNetworkChart' && chart.chartType !== 'Stacked100BarChartHorizontal' &&
          chart.chartType !== 'StackedBarChartHorizontal' &&
          chart.chartType !== 'WaterFall' && chart.chartType !== 'GeoMapChart' && chart.chartType !== 'BubbleChart' && chart.chartType !== 'Matrix';
      const hasDataSankeyChart = chart.chartType === 'SankeyChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.legendColumns.length > 0;
      const hasCollapsableNetworkChart = chart.chartType === 'CollapsableNetworkChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.legendColumns.length > 0;
      const hasDataKPIChart = chart.chartType === 'KPIChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.valueColumns.length > 0;
      const hasDataStacked100BarChartHorizontal = chart.chartType === 'Stacked100BarChartHorizontal' &&
          chart.chartDataSettings.valueColumns.length > 0 &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataStackedBarChartHorizontal = chart.chartType === 'StackedBarChartHorizontal' &&
          chart.chartDataSettings.valueColumns.length > 0 &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataWaterfall = chart.chartType === 'WaterFall' &&
        chart.chartDataSettings.valueColumns.length > 0 &&
        chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataGeoMapChart = chart.chartType === 'GeoMapChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0;
    const hasDataBubbleMapChart = chart.chartType === 'BubbleChart' &&
      chart.chartDataSettings.descriptorColumns.length > 0;
    const hasDataMatrix = chart.chartType === 'Matrix' &&
      chart.chartDataSettings.descriptorColumns.length > 0 && chart.chartDataSettings.valueColumns.length > 0;
      return hasData || hasDataSankeyChart || hasCollapsableNetworkChart ||
          hasDataKPIChart || hasDataStacked100BarChartHorizontal || hasDataStackedBarChartHorizontal ||
          hasDataWaterfall || hasDataGeoMapChart || hasDataMatrix || hasDataBubbleMapChart;
  }

  addLegendColumn(column: Partial<LegendColumn>, chart: Partial<ChartModel>, columnType: ColumnType): void {
      if(chart.chartType === 'GaugeChart' ||
        chart.chartType === 'AdvancedGaugeChart') {
          column.columnAggregationType = column.dataModuleType === 'Quantity' ? 'Sum' : 'Count';
      }
      if(chart.chartType === 'ScatterChart' || chart.chartType === 'SankeyChart' || chart.chartType === 'CollapsableNetworkChart' ||
        chart.chartType === 'BarChart' || chart.chartType === 'StackedBarChart' ||
        chart.chartType === 'Stacked100BarChart' || chart.chartType === 'BarChartHorizontal' ||
        chart.chartType === 'StackedBarChartHorizontal' || chart.chartType === 'Stacked100BarChartHorizontal' ||
        chart.chartType === 'TreeMap' || chart.chartType === 'ClusteredColumnLineChart' ||
        chart.chartType === 'StackedColumnLineChart' || chart.chartType === 'AreaChart' ||
        chart.chartType === 'LineStackChart' ||
        chart.chartType === 'LineChart' || chart.chartType === 'Matrix'){
          column.columnAggregationType = 'NoAggregation';
      }
      chart.isLoading = true;
    this.dashboardApi.addLegendColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addLegendColumn(column, chart.chartId);
          if(this.shouldLoadChartDataForLegendColumn(chart)) {
              this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
          } else {
            chart.isLoading = false;
          }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  shouldLoadChartDataForLegendColumn(chart: Partial<ChartModel>): boolean {
      const hasData = chart.chartType !== 'SankeyChart' && chart.chartType !== 'CollapsableNetworkChart' &&
          chart.chartType !== 'Stacked100BarChartHorizontal' && chart.chartType !== 'StackedBarChartHorizontal' &&
          chart.chartType !== 'GeoMapChart' && chart.chartType !== 'BubbleChart';
      const hasDataSankeyChart = chart.chartType === 'SankeyChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.legendColumns.length > 0;
      const hasCollapsableNetworkChart = chart.chartType === 'CollapsableNetworkChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.legendColumns.length > 0;
      const hasDataStacked100BarChartHorizontal = chart.chartType === 'Stacked100BarChartHorizontal' &&
          chart.chartDataSettings.valueColumns.length > 0 && chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataStackedBarChartHorizontal = chart.chartType === 'StackedBarChartHorizontal' &&
          chart.chartDataSettings.valueColumns.length > 0 && chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataGeoMapChart = chart.chartType === 'GeoMapChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataBubbleMapChart = chart.chartType === 'BubbleChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      return hasData || hasDataSankeyChart || hasCollapsableNetworkChart || hasDataStacked100BarChartHorizontal ||
          hasDataStackedBarChartHorizontal || hasDataGeoMapChart || hasDataBubbleMapChart;
  }
  deleteSecondValueColumnObs(column: Partial<ValueColumn>, chart: Partial<ChartModel>) {
    chart.isLoading = true;

    return this.dashboardApi.deleteSecondValueColumn(
      column,
      this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,
      chart.chartId
    ).pipe(
      tap((res: any) => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteSecondValueColumn(chart.chartId, column.id);
      })
    );
  }

  addValueColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                 columnType: ColumnType, index: number): void {
    if (chart.chartType === 'TableChart') {
      if(columnType === 'Measure'){
        column.columnAggregationType = 'NoAggregationForMeasure';
      }else {
        column.columnAggregationType = column.dataModuleType === 'Quantity' ? 'Sum' : 'NoAggregation';
      }
      column.drillLevel = 1;
    } else if(columnType === 'Measure'){
      column.columnAggregationType = 'NoAggregationForMeasure';
    } else {
      column.columnAggregationType = column.dataModuleType === 'Quantity' ? 'Sum' : 'Count';
    }
    chart.isLoading = true;
    this.dashboardApi.addValueColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.showAsPercentageOfTotal = res.data.showAsPercentageOfTotal;
        column.columnAggregationType = res.data.columnAggregationType;
        column.columnType = columnType;
        column.conditionalFormatting = res.data.conditionalFormatting;
        this.updateShouldSaveDashboard();
        this.dashboardState.addValueColumn(column, chart.chartId, index);
        if (this.shouldLoadChartDataForValueColumn(chart)) {
          this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        } else {
          chart.isLoading = false;
        }
        if ((chart.chartType === 'KPIChart' && res.data?.hasWarning)) {
          this.toastr.showWarning(res.data.warningMessage);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  shouldLoadChartDataForValueColumn(chart: Partial<ChartModel>): boolean {
      const hasData = chart.chartType !== 'SankeyChart' && chart.chartType !== 'CollapsableNetworkChart' &&
          chart.chartType !== 'KPIChart' && chart.chartType !== 'Stacked100BarChartHorizontal' &&
          chart.chartType !== 'StackedBarChartHorizontal' && chart.chartType !== 'WaterFall' &&
          chart.chartType !== 'GeoMapChart' && chart.chartType !== 'BubbleChart';
      const hasDataSankeyChart = chart.chartType === 'SankeyChart' && chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.legendColumns.length > 0;
      const hasDataCollapsableNetworkChart = chart.chartType === 'CollapsableNetworkChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.legendColumns.length > 0;
      const hasDataKPIChart = chart.chartType === 'KPIChart' &&
          chart.chartDataSettings.valueColumns.length > 0 &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataStacked100BarChartHorizontal = chart.chartType === 'Stacked100BarChartHorizontal' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.valueColumns.length > 0;
      const hasDataStackedBarChartHorizontal = chart.chartType === 'StackedBarChartHorizontal' &&
          chart.chartDataSettings.descriptorColumns.length > 0 &&
          chart.chartDataSettings.valueColumns.length > 0;
      const hasDataWaterfall = chart.chartType === 'WaterFall' &&
        chart.chartDataSettings.valueColumns.length > 0 &&
        chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataGeoMapChart = chart.chartType === 'GeoMapChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      const hasDataBubbleMapChart = chart.chartType === 'BubbleChart' &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      return hasData || hasDataSankeyChart || hasDataCollapsableNetworkChart || hasDataKPIChart ||
          hasDataStacked100BarChartHorizontal || hasDataStackedBarChartHorizontal ||
          hasDataWaterfall || hasDataGeoMapChart || hasDataBubbleMapChart;
  }

  addSecondValueColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                       columnType: ColumnType, index: number): void {
    if(chart.chartType !== 'BubbleChart'){
      if(columnType === 'Measure'){
        column.columnAggregationType = 'NoAggregationForMeasure';
      }else {
        column.columnAggregationType = column.dataModuleType === 'Quantity' ? 'Sum' : 'Count';
      }
    }else{
      column.columnAggregationType = 'NoAggregation';
    }
    chart.isLoading = true;
    this.dashboardApi.addSecondValueColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.showAsPercentageOfTotal = res.data.showAsPercentageOfTotal;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addSecondValueColumn(column, chart.chartId, index);
        if (this.shouldLoadChartDataForSecondValueColumn(chart)) {
          this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        } else {
          chart.isLoading = false;
        }
        if ((chart.chartType === 'KPIChart' && res.data?.hasWarning)) {
          this.toastr.showWarning(res.data.warningMessage);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  shouldLoadChartDataForSecondValueColumn(chart: Partial<ChartModel>): boolean {
      const hasData = chart.chartType !== 'KPIChart';
      const hasDataKPIChart = chart.chartType === 'KPIChart' && chart.chartDataSettings.valueColumns.length > 0 &&
          chart.chartDataSettings.descriptorColumns.length > 0;
      return hasData || hasDataKPIChart;
  }

  addSourceNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                      columnType: ColumnType, index: number): void{
    chart.isLoading = true;
    this.dashboardApi.addSourceNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addSourceNameColumn(column, chart.chartId,index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateSourceNameColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateSourceNameColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.dashboardState.updateSourceNameColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteSourceNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteSourceNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteSourceNameColumn(chart.chartId, column.id);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addTargetNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                      columnType: ColumnType, index: number): void{
    chart.isLoading = true;
    this.dashboardApi.addTargetNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addTargetNameColumn(column, chart.chartId,index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateTargetNameColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateTargetNameColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateTargetNameColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteTargetNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>) {
    chart.isLoading = true;
    this.dashboardApi.deleteTargetNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteTargetNameColumn(chart.chartId, column.id);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addSourceGroupColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                      columnType: ColumnType, index: number): void{
    chart.isLoading = true;
    this.dashboardApi.addSourceGroupColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addSourceGroupColumn(column, chart.chartId,index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateSourceGroupColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateSourceGroupColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateSourceGroupColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteSourceGroupColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteSourceGroupColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteSourceGroupColumn(chart.chartId, column.id);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addSourceGroupNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                       columnType: ColumnType, index: number): void {
    chart.isLoading = true;
    this.dashboardApi.addSourceGroupNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addSourceGroupNameColumn(column, chart.chartId,index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateSourceGroupNameColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateSourceGroupNameColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateSourceGroupColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteSourceGroupNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteSourceGroupNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteSourceGroupNameColumn(chart.chartId, column.id);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addTargetGroupColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                           columnType: ColumnType, index: number): void{
    chart.isLoading = true;
    this.dashboardApi.addTargetGroupColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addTargetGroupColumn(column, chart.chartId,index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateTargetGroupColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateTargetGroupColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateTargetGroupColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteTargetGroupColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
    chart.isLoading = true;
    this.dashboardApi.deleteTargetGroupColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteTargetGroupColumn(chart.chartId, column.id);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addTargetGroupNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>,
                       columnType: ColumnType, index: number): void {
    chart.isLoading = true;
    this.dashboardApi.addTargetGroupNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addTargetGroupNameColumn(column, chart.chartId,index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateTargetGroupNameColumn(newColumn: Partial<DescriptorColumn>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateTargetGroupNameColumn(newColumn, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateTargetGroupNameColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteTargetGroupNameColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>) {
    chart.isLoading = true;
    this.dashboardApi.deleteTargetGroupNameColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteTargetGroupNameColumn(chart.chartId, column.id);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addTooltipColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>, columnType, index: number): void {
      if(columnType === 'Measure'){
          column.columnAggregationType = 'NoAggregationForMeasure';
      } else {
          column.columnAggregationType = column.dataModuleType === 'Quantity' ?
              'Sum' : (chart.chartType === 'TableChart' ? 'NoAggregation' : 'Count');
      }
      chart.isLoading = true;
      this.dashboardApi.addToolTipColumn(column, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
          (res) => {
              column.id = res.data.id;
              column.formatShow = res.data.formatShow;
              column.showAsPercentageOfTotal = res.data.showAsPercentageOfTotal;
              column.columnAggregationType = res.data.columnAggregationType;
              column.columnType = columnType;
              column.conditionalFormatting = res.data.conditionalFormatting;
              this.updateShouldSaveDashboard();
              this.dashboardState.addTooltipColumn(column, chart.chartId, index);
              this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

    updateTooltipColumn(newColumn: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
        this.dashboardApi.updateTooltipColumn(newColumn, this.dashboardState.workspaceId, this.dashboardState.dashboardId,
            chart.chartId).subscribe(
            (res) => {
                this.updateShouldSaveDashboard();
                this.dashboardState.updateTooltipColumn(chart.chartId, newColumn.id, newColumn);
                this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
            },
            (errorRes) => {
                this.toastr.showError(errorRes.error.message);
            },
        );
    }

    changeTooltipColumnPriority(tooltipColumn: Partial<ValueColumn>, newPriority: number, chart: Partial<ChartModel>,
                              previousIndex: number,currentIndex: number): void{
        this.dashboardApi.changeTooltipColumnPriority(tooltipColumn, newPriority,
            this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart).subscribe(
            () => {
                this.updateShouldSaveDashboard();
                this.dashboardState.changeTooltipColumnPriority(tooltipColumn, newPriority, currentIndex,
                    previousIndex, chart.chartId);
                this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
            },
            (errorRes) => {
                this.toastr.showError(errorRes.error.message);
            },
        );
    }

    deleteTooltipColumn(column: Partial<ValueColumn>, chart: Partial<ChartModel>): void {
        chart.isLoading = true;
        this.dashboardApi.deleteTooltipColumn(column, this.dashboardState.workspaceId,
            this.dashboardState.dashboardId, chart.chartId).subscribe(
            () => {
                this.updateShouldSaveDashboard();
                this.dashboardState.deleteTooltipColumn(chart.chartId, column.id);
                this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
            },
            (errorRes) => {
                this.toastr.showError(errorRes.error.message);
            },
        );
    }

  addControllerColumn(column: Partial<DescriptorColumn>, chart: Partial<ChartModel>,
                      columnType: ColumnType): void {
    chart.isLoading = true;
    this.dashboardApi.addControllerColumn(column, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId).subscribe(
      (res) => {
        column.id = res.data.id;
        column.formatShow = res.data.formatShow;
        column.isAscending = true;
        column.columnType = columnType;
        this.updateShouldSaveDashboard();
        this.dashboardState.addDescriptorColumn(column, chart.chartId,0);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  //charts
  loadChartsPermissions(dashboard: Dashboard): void {
      this.dashboardApi.getPermissionsOnEachChartInDetail(dashboard.workspaceId, dashboard.dashboardId).subscribe(
          (response) => {
              dashboard = this.updateDashboardChartsPermissions(dashboard,response.data);
              dashboard = this.updateDashboardChartsDesignVersion(dashboard);
              dashboard = this.jsonifyAnnotaionSettings(dashboard);
              dashboard.charts = this.dashboardState.addControllersValuesToCharts(dashboard.charts);
              this.setInitialStatesForDashboard(dashboard);
              this.dashboardState.updateDashboard(dashboard);
              this.loadDashboardCharts(dashboard);
              this.dashboardState.setControllersFilters();
              this.dashboardState.dashboardSetUpdating(false);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  addControllersValuesToCharts(){
    const dashboard = this.dashboardState.getDashboardValue()
    dashboard.charts = this.dashboardState.addControllersValuesToCharts(dashboard.charts, true);
    this.setInitialStatesForDashboard(dashboard)
    this.dashboardState.updateDashboard(dashboard);
    this.dashboardState.setControllersFilters();
    this.dashboardState.dashboardSetUpdating(false);
  }

  updateDashboardChartsDesignVersion(dashboard: Dashboard): Dashboard {
      dashboard.charts.forEach((chart, chartIndex) => {
          chart.chartDesignSettings = this.jsonAdapterService.jsonify<ChartDesignSettings>(chart.chartDesignSettings);
          chart.dashboardId = dashboard.dashboardId;
          chart.workspaceId = dashboard.workspaceId;
          if (this.chartDesignVersionManager.needUpdate(chart.chartDesignSettings?.version)) {
              chart.chartDesignSettings = this.chartDesignVersionManager.updateDesignSettings(chart);
          }
          dashboard.charts[chartIndex] = chart;
      });
      return dashboard;
  }

  jsonifyAnnotaionSettings(dashboard: Dashboard): Dashboard {
    dashboard.charts.forEach((chart, chartIndex) => {
      if (chart?.chartAnnotationSettings) {
        chart.chartAnnotationSettings =
          this.jsonAdapterService.jsonify<ChartAnnotationSetting>(chart.chartAnnotationSettings);
        dashboard.charts[chartIndex] = chart;
      }
    });
    return dashboard;
  }

  updateDashboardGridVersion(dashboard: Dashboard): Dashboard {
    const dashboardChartsGridJsonfield = this.jsonAdapterService.jsonify<DashboardGrid>(dashboard.chartsGrid);
    if (!dashboardChartsGridJsonfield) {
      dashboard.chartsGrid = {version: '0.0.2', grid: [], layoutVersion: '0.0.1'};
          this.loadDashboardCharts(dashboard);
      } else if (this.gridVersionManager.needUpdate(dashboardChartsGridJsonfield.version)) {
          dashboard.chartsGrid = this.gridVersionManager.updateGridVersion(dashboardChartsGridJsonfield);
          this.dashboardApi.updateDashboardVersioningProperties(this.dashboardState.workspaceId, dashboard.dashboardId,
              dashboard.dashboardDesignSettings, dashboard.chartsGrid).subscribe();
      } else {
          dashboard.chartsGrid = dashboardChartsGridJsonfield;
      }
      return dashboard;
  }

  /**
   * fixing issue with no scenario (TART-8835) it happens some time and makes grid field empty
   * this function migrates broken dashboard
   * @param dashboard
   */
  public static fixEmptyGridIssue(dashboard: Dashboard){
    const dashboardChartsGridJsonfield = JsonAdapterService.staticJsonify<DashboardGrid>(dashboard.chartsGrid);
    if((dashboard?.charts?.length>1 && dashboardChartsGridJsonfield?.grid?.length == 0)
      ||
      (dashboard?.charts?.length && dashboard?.charts?.length>dashboardChartsGridJsonfield?.grid?.length &&
        dashboard?.charts?.length-dashboardChartsGridJsonfield?.grid?.length>1
      )
    ){
      console.info('Attempting to fix the empty grid issue');
      dashboard.charts.forEach((item, index)=>{
        const newLayoutSizes = GridItemFactoryService.getLayoutVersion02GridSizes(item.chartType);
        const gridTemp = {
          type: item.isController?1:0,
          cols: newLayoutSizes?.cols||25,
          rows: newLayoutSizes?.rows||25,
          // x:0,
          // y:10,
          id:item.chartId,
          minItemRows: 1,
          minItemCols: 3,
          maxItemCols: 10000,
          maxItemRows: 10000,
        };
        if(!dashboardChartsGridJsonfield.grid){
          dashboardChartsGridJsonfield.grid = [];
        }
        dashboardChartsGridJsonfield.grid.push(gridTemp as any)
      });
      dashboardChartsGridJsonfield.grid = _.uniqBy(dashboardChartsGridJsonfield.grid, 'id');
      return dashboardChartsGridJsonfield;
    }else if(dashboard?.charts?.length && dashboardChartsGridJsonfield?.grid?.length && dashboard?.charts?.length<dashboardChartsGridJsonfield?.grid?.length){
      console.info('Attempting to fix grid.length>dashboard.charts.length issue');
        const chartIds = dashboard?.charts?.map(item=>item.chartId);
        let result = [];
        _.forEach(chartIds, function (n, key) {
          _.forEach(dashboardChartsGridJsonfield.grid, function (n2, key2) {
            if (n === n2.id) {
              result.push(n2);
            }
          });
        });
        dashboardChartsGridJsonfield.grid = _.uniqBy(result, 'id');
        return dashboardChartsGridJsonfield;
    }
  }


  updateDashboardDesignSettingsVersion(dashboard: Dashboard): Dashboard {
      dashboard.dashboardDesignSettings = this.jsonAdapterService
          .jsonify<DashboardDesignSetting>(dashboard.dashboardDesignSettings);
      if (this.dashboardDesignSettingsVersionManagerService.needUpdate(dashboard.dashboardDesignSettings.version)) {
          dashboard.dashboardDesignSettings = this.dashboardDesignSettingsVersionManagerService
              .updateDesignSettings(dashboard);
      }
      return dashboard;
  }

  updateDashboardChartsPermissions(dashboard: Dashboard, chartsPermissions) {
      dashboard.charts.forEach((chart, index, self) => {
          const chartIndex = chartsPermissions.findIndex(permission => permission.chartId === chart.chartId);
          const chartPermissions = chartIndex > -1 ? chartsPermissions[chartIndex].permissions : null;
          self[index] = this.chartPermissionService.addPermissionToEntity(chart,
              chartPermissions, this.userService.getUserMinInfoValue(), false);
      });
      return dashboard;
  }

  setInitialStatesForDashboard(dashboardTemp: Dashboard): void {
      this.dashboardState.setDefaultChartsDrillDownValuesState(0);
      this.dashboardState.setInitialChartsDrillDownMode(dashboardTemp.charts.length);
      this.dashboardState.setInitialChartsUserFilters(dashboardTemp.charts.length);
      this.dashboardState.setInitialChartsZoomStatus(dashboardTemp.charts.length);
  }

  loadDashboardCharts(dashboard: Dashboard): void {
    const defaultDashboardLoadType:any = localStorage.getItem('dashboardLoadType')
    const loadType: 'hybrid'|'parallel'|'sequential' = defaultDashboardLoadType || 'parallel';
    console.info('Dashboard is running in '+loadType +' mode.')
    localStorage.setItem('dashboardLoadType', loadType)
    this.chartsToLoadList = [];
    this.chartsToLoadInParallelModeList = [];
    const parallelCharts:ChartType[] = [
      'TextChart',
      'ImageChart',
      'IframeChart',
      'GaugeChart',
      'AdvancedGaugeChart',
      'KPIChart',
      'CardChart',
      'AdvancedCardChart',
      'ButtonChart'
    ];
    const gridIndexChartIdMap = {}
    dashboard.chartsGrid.grid.map((item, index)=>{
      gridIndexChartIdMap[item.id] = index;
    });
    const sortedCharts = []
    dashboard.charts.forEach(item=>{
      if(gridIndexChartIdMap[item.chartId] || gridIndexChartIdMap[item.chartId] === 0){
        sortedCharts[gridIndexChartIdMap[item.chartId]] = item
      }
    });
    // dashboard.chartsGrid.grid.forEach((grid,index,self) => {})
    sortedCharts.forEach(
      (chart, index) => {
        // setTimeout(()=>{
          if(chart.chartType === 'TextChart'){
            chart.chartDesignSettings.chart.text = chart.chartDesignSettings.chart.text.replaceAll('tartHref=','href=');
          }

          // if hasDefaultDatasource there in response:
          if (chart.hasDefaultDatasource !== undefined) {
            if (chart.hasDefaultDatasource) {
              chart.chartDesignSettings.chart.datasourceDateToggle = true;
              this.dashboardState.setChartDatasourceState(chart.chartId, {
                hasDefault: true,
                tickTime: chart.lastModifiedDataSourceTickTime || null
              });
            }
            //  else {
            //   chart.chartDesignSettings.chart.datasourceDateToggle = false;
            //   this.dashboardState.setChartDatasourceState(chart.chartId, {
            //     hasDefault: false,
            //     tickTime: null
            //   });
            // }
            this.dashboardState.updateOneChart(chart);
          }

          if(!chart.hasError &&
            (((chart.chartDataSettings.descriptorColumns.length + chart.chartDataSettings.legendColumns.length > 0
              || chart.chartType === 'CardChart' || chart.chartType === 'AdvancedCardChart' || chart.chartType === 'TableChart' ||
              chart.chartType === 'GaugeChart' || chart.chartType === 'AdvancedGaugeChart') &&
              chart.chartType !== 'SankeyChart' && chart.chartType !== 'KPIChart' &&
              chart.chartType !== 'CollapsableNetworkChart' && chart.chartType !== 'GeoMapChart' && chart.chartType !== 'BubbleChart' &&
              chart.chartDataSettings.valueColumns.length + chart.chartDataSettings.secondValueColumns.length > 0) ||
              (chart.isController && chart.chartDataSettings.descriptorColumns.length > 0) ||
              ((chart.chartType === 'SankeyChart' || chart.chartType === 'CollapsableNetworkChart')
                && chart.chartDataSettings.descriptorColumns.length > 0 &&
                chart.chartDataSettings.legendColumns.length > 0) ||
              (chart.chartType === 'KPIChart' && chart.chartDataSettings.descriptorColumns.length > 0 &&
                chart.chartDataSettings.valueColumns.length > 0) || (chart.chartType === 'GeoMapChart' &&
                chart.chartDataSettings.descriptorColumns.length > 0)|| (chart.chartType === 'BubbleChart' &&
                chart.chartDataSettings.descriptorColumns.length > 0))){
            if(loadType === "hybrid"){
              if(chart.isController || parallelCharts.includes(chart.chartType)){
                this.loadChartData(dashboard.workspaceId, dashboard.dashboardId, chart);

              }else{
                this.chartsToLoadList.push({
                  workspaceId: dashboard.workspaceId,
                  dashboardId: dashboard.dashboardId,
                  chart
                });
                chart.isLoading = true;
                this.dashboardState.updateOneChart(chart);
              }
            }else if(loadType === "sequential"){
              this.chartsToLoadList.push({
                workspaceId: dashboard.workspaceId,
                dashboardId: dashboard.dashboardId,
                chart
              });
              chart.isLoading = true;
              this.dashboardState.updateOneChart(chart);
            }else{
              this.loadChartData(dashboard.workspaceId, dashboard.dashboardId, chart);

            }
            // this.loadChartData(dashboard.workspaceId, dashboard.dashboardId, chart);
          } else if(chart.chartType === 'SankeyChart' || chart.chartType === 'KPIChart') {
            this.dashboardState.updateChartData([], chart.chartId);
          }
        // }, 15000*(index))
      },
    );
    this.chartsToLoadListIndex = 0
    const selectedChart = this.chartsToLoadList[this.chartsToLoadListIndex]
    this.loadMyChart(selectedChart)

  }

  loadMyChart(data:any){
    if(!data){
      return;
    }
    const {workspaceId, dashboardId, chart} = data
    this.loadChartData(workspaceId, dashboardId, chart,false,false,undefined, ()=>{
      if(this.chartsToLoadListIndex < this.chartsToLoadList.length){
        this.chartsToLoadListIndex++;
        const selectedChart = this.chartsToLoadList[this.chartsToLoadListIndex]
        this.loadMyChart(selectedChart)
      }
    });
  }

  relaodHasErroredCharts(datasourceId: string): void {
    const dashboard = this.dashboardState.getDashboardValue();
    dashboard.charts.forEach(chart => {
      if (chart.hasError && this.dashboardState.checkChartShouldReload(chart, datasourceId)) {
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      };
    });
  }

  loadChartsDataOnUpdateControllersValueForButtonChart(){
      this.loadChartsDataOnUpdateControllersValue(this.dashboardState.getDashboardValue().charts, this.dashboardState.workspaceId, this.dashboardState.dashboardId, 'ButtonChart')
  }

  loadChartsDataOnUpdateControllersValue(charts: Partial<ChartModel>[], workspaceId: string, dashboardId: string, callerType?:string): void {
    const hasBtnChart = (charts.findIndex(item=>item.chartType === "ButtonChart") !== -1);
    const shouldRunFunction = ((hasBtnChart && callerType === 'ButtonChart') || !hasBtnChart);
    if(!shouldRunFunction){
      return;
    }
    const controllersShouldLoadChartsData = this.dashboardState.controllersShouldLoadChartsData();
      let chartsNeedToLoadData = [];
      if(controllersShouldLoadChartsData) {
          chartsNeedToLoadData = charts.filter(chart =>
                  this.dashboardState.selectedControllerValue?.controllerValue.controllerId !== chart.chartId);
      }
      if(this.dashboardState.selectedControllerValue?.isInteractableWithCharts &&
          !this.dashboardState.updateChartInteractAbilityStatus) {
          chartsNeedToLoadData = chartsNeedToLoadData.filter(chart => {
              const chartInteractWithController = this.dashboardState.selectedControllerValue
                  .otherChartsInteractionStatusWithThis.find(chartStatus => chartStatus.chartId === chart.chartId);
              if((chartInteractWithController === undefined ||
                      chartInteractWithController?.interactStatusType !== 'NoInteraction')) {
                  return chart;
              }
          });
      } else if(this.dashboardState.updateChartInteractAbilityStatus) {
          this.dashboardState.updateChartInteractAbilityStatus = false;
      }
      chartsNeedToLoadData.forEach(chart => {
          const generalControllerCondition = (chart.isController && chart.chartDataSettings.descriptorColumns.length > 0);
          const generalChartCondition = ((chart.chartDataSettings.descriptorColumns.length +
                      chart.chartDataSettings.legendColumns.length > 0 ||
                      chart.chartType === 'CardChart' || chart.chartType === 'AdvancedCardChart' || chart.chartType === 'TableChart' ||
                      chart.chartType === 'GaugeChart' || chart.chartType === 'AdvancedGaugeChart') &&
                  chart.chartDataSettings.valueColumns.length + chart.chartDataSettings.secondValueColumns.length > 0) &&
              chart.chartType !== 'SankeyChart';
          const sankeyChartCondition =
              chart.chartDataSettings.descriptorColumns.length + chart.chartDataSettings.legendColumns.length > 0 &&
              chart.chartType === 'SankeyChart';
          if (generalChartCondition || generalControllerCondition || sankeyChartCondition) {
              this.loadChartData(workspaceId, dashboardId, chart);
          }
      });
  }

  loadChart(workspaceId: string, dashboardId: string, chartId: string) {
      this.dashboardApi.getChart(workspaceId, dashboardId, chartId).subscribe(
          (response) => {
              let chart = response.data;
              if (chart.chartType === 'ShapeMap' || chart.chartType === 'GeoMapChart' || chart.chartType === 'BubbleChart'){
                  this.loadMapsList(chart);
                  this.loadMapKeys(chart);
              }
              chart.chartDesignSettings = this.jsonAdapterService.jsonify<ChartDesignSettings>(chart.chartDesignSettings);
              chart = this.chartPermissionService.addPermissionToEntity(chart, chart.permissions,
                  this.userService.getUserMinInfoValue(), false);
              this.dashboardState.createChart(chart);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getDashboardCharts$(): Observable<Partial<ChartModel>[]> {
    return this.dashboardState.getCharts$();
  }

  getChartValue(chartId: string): Partial<ChartModel> {
    return this.dashboardState.getChartValue(chartId);
  }

  getControllersFilters$(): Observable<DashboardControllersFilter[]> {
    return this.dashboardState.getControllersFilters$();
  }

  getChartsFilters$(): Observable<DashboardControllersFilter[]> {
    return this.dashboardState.getChartFilterForDisplay$();
  }

  getChartDrillingFilterValuesByChartIndex(chartIndex: number){
    return this.dashboardState.getValidDrillingFilterValues(chartIndex);
  }

  //chart
  loadChartData(workspaceId: string,
                dashboardId: string,
                chart: Partial<ChartModel>,
                isNewChart = false,
                appendData = false,
                interactionValues: InteractionValues[] = undefined, cb?:any ): void {
    interactionValues = this.dashboardState.getValidInteractionValues(chart,interactionValues);
      const drillingFilterValues = this.dashboardState.getValidDrillingFilterValues(chart.chartIndexInDashboard);
      if(!chart?.paginationValues && chart.isController && chart.chartType === 'DropdownController') {
          chart.paginationValues = {
              searchKey: null,
              needNewQuery: true,
              values: null,
          };
      }
    // if table chart is in pagination mode
      if(chart?.chartType === 'TableChart' && chart?.chartDesignSettings?.chart?.isPaginated && (!chart?.paginationValues || !appendData)){
        chart.paginationValues = {
          rowNumber: chart?.chartDesignSettings?.chart?.linesInPage
        }
      }
      if(!appendData) {
          chart.isLoading = true;
          this.dashboardState.updateOneChart(chart);
      }
      this.dashboardState.getDrillThroughChartFilters$().pipe(take(1)).subscribe(
        chartFilters => {
          this.dashboardApi.loadChartData(workspaceId, dashboardId, chart, chart.controllersValues, interactionValues,
              drillingFilterValues, chartFilters ).subscribe(
              (res) => {
                // save hasDefaultDatasource in chart model
                chart.hasDefaultDatasource = res.hasDefaultDatasource;

                // if hasDefaultDatasource true toggle on and loadDefaultDatasourceChart calls:
                if (res.data.hasDefaultDatasource) {
                  chart.chartDesignSettings.chart.datasourceDateToggle = true;
                  this.dashboardState.setChartDatasourceState(chart.chartId, {
                    hasDefault: true
                  });
                  // this.loadDefaultDatasourceChart(
                  //   chart.workspaceId,
                  //   chart.dashboardId,
                  //   chart.chartId
                  // );
                }
                else {
                  if (chart.chartDesignSettings.chart.datasourceDateToggle) {
                    chart.chartDesignSettings.chart.datasourceDateToggle = false;
                    this.dashboardState.setChartDatasourceState(chart.chartId, {
                      hasDefault: false
                    });
                  } else {
                    this.dashboardState.setChartDatasourceState(chart.chartId, {
                      hasDefault: false
                    });
                  }
                }

                const columnsData = res.data ? res.data.columnsData : [];
                if (columnsData && columnsData.length > 0) {
                  const hasAnyData = columnsData.some(column =>
                    column.data && column.data.length > 0
                  );
                  chart.noDataExist = !hasAnyData;
                }
                // else {
                //   charts[index].noDataExist = true;
                // }
                // console.log("Chart ID:", chart.chartId, "noDataExist:", chart.noDataExist, "columnsData length:", columnsData.length);
                  if(chart.chartType === 'ShapeMap' || chart.chartType === 'GeoMapChart' || chart.chartType === 'BubbleChart'){
                      const descriptorColumn = res.data?.columnsData?.find(item=>item.chartColumnType === 'Descriptor')
                      this.dashboardApi.loadMapFile(chart, descriptorColumn).subscribe(
                          (mapFile)=>{
                              this.dashboardState.updateChartData(res.data ? res.data.columnsData : [],
                                  chart.chartId, mapFile);
                              const chartValue = this.getChartValue(chart.chartId);
                              if (isNewChart) {
                                  this.setSettingSidebarId(chartValue.chartType);
                                  this.dashboardState.updateOneChart(chart);
                              }
                              if (res.data.hasDefaultDatasource && chartValue) {
                                chartValue.chartDesignSettings.chart.datasourceDateToggle = true;
                                chartValue.hasDefaultDatasource = true;
                              }
                              if(chart.chartType === 'ShapeMap' && chart.chartDataSettings.descriptorColumns.length === 1){
                                if(descriptorColumn.mapFilePath){
                                  chartValue.chartDataSettings.descriptorColumns[0].mapId = descriptorColumn.mapId
                                  chartValue.chartDataSettings.descriptorColumns[0].mapFilePath = descriptorColumn.mapFilePath
                                }else{
                                  chartValue.chartDataSettings.descriptorColumns[0].mapId = chart.mapId
                                  chartValue.chartDataSettings.descriptorColumns[0].mapFilePath = chart.mapFilePath
                                }
                              }
                            this.dashboardState.updateOneChart(chartValue);
                          },
                          (errorRes) => {
                              this.dashboardState.setChartError(chart.chartId, errorRes.error.message);
                              this.toastr.showError(errorRes.error.message);
                          },
                      );

                  }else{
                    if (appendData) {
                        if(chart?.chartType === 'TableChart' && chart?.chartDesignSettings?.chart?.isPaginated){
                          this.dashboardState.updateChartData(res.data ? res.data.columnsData : [], chart.chartId, null,  null, res?.data?.totalRowCount);
                        }else{
                          chart.paginationValues = undefined;
                          this.dashboardState.appendChartData(res.data ? res.data.columnsData : [], chart.chartId, res?.data?.totalRowCount);
                        }
                      }else if(chart.chartType === 'Matrix'){
                        this.dashboardState.updateChartData(res.data ? res.data.columnsData : [], chart.chartId,
                          null, res.data.result);
                      }else {
                          this.dashboardState.updateChartData(res.data ? res.data.columnsData : [], chart.chartId, null,  null, res?.data?.totalRowCount);
                      }
                      const chartValue = this.getChartValue(chart.chartId);
                      if (isNewChart) {
                          this.setSettingSidebarId(chartValue.chartType);
                          this.dashboardState.updateOneChart(chart);
                      }
                      if (res.data.hasDefaultDatasource && chartValue) {
                        chartValue.chartDesignSettings.chart.datasourceDateToggle = true;
                        chartValue.hasDefaultDatasource = true;
                      }
                      this.dashboardState.updateOneChart(chartValue);
                      this.handleChartsIssue(res, chart);
                  }
                  if(cb){
                    cb()
                  }
              },
              (errorRes) => {
                if (errorRes.status) {
                  this.dashboardState.setChartError(chart.chartId, errorRes.error.message);
                  this.toastr.showError(errorRes.error.message);
                } else {
                  this.dashboardState.setChartNeedToReload(chart.chartId);
                }
                if(cb){
                  cb()
                }
              },
          );
        },
      );
  }
  exportCsvTableChartData(
    workspaceId: string,
    dashboardId: string,
    chart: Partial<ChartModel>,
    interactionValues: InteractionValues[] = undefined,
    cb?: any
  ): void {
    if (chart.chartType !== 'TableChart') {
      console.warn('This method only supports TableChart.');
      return;
    }

    interactionValues = this.dashboardState.getValidInteractionValues(chart, interactionValues);
    const drillingFilterValues = this.dashboardState.getValidDrillingFilterValues(chart.chartIndexInDashboard);

    this.dashboardState.getDrillThroughChartFilters$().pipe(take(1)).subscribe(chartFilters => {
      this.dashboardApi
        .loadExportCsvTableChartData(
          workspaceId,
          dashboardId,
          chart,
          chart.controllersValues,
          interactionValues,
          drillingFilterValues,
          chartFilters
        )
        .subscribe(
          (res) => {
            if (res?.isSuccess && res?.data) {
              const downloadUrl = this.baseAddress + res.data;
              window.open(downloadUrl, '_blank');
            } else {
              this.toastr.showError(res?.message || 'دانلود ناموفق.');
            }

            if (cb) cb();
          },
          (errorRes) => {
            this.toastr.showError(errorRes?.error?.message || 'دانلود ناموفق فایل.');
            if (cb) cb();
          }
        );
    });
  }

  updateRollbackChartSetting(chart: Partial<ChartModel>): void {
    this.dashboardState.updateRollbackChartDesignSetting(chart);
    this.dashboardState.updateRollbackChartAnnotationSetting(chart);
  }
  setChartDesign(newChart: Partial<ChartModel>, hasChangedDesignSetting: boolean = true): void {
    if(hasChangedDesignSetting){
      this.dashboardState.hasChangedDesignSetting = hasChangedDesignSetting;
      this.updateShouldSaveDashboard();
    }
    this.dashboardState.updateOneChart(newChart);
  }
  updateChartDesign(shouldDisableSaveAlert?:boolean): void {
    if (this.dashboardState.getSelectedChartValue() && this.dashboardState.getRollbackChartDesignSettingValue() &&
      this.dashboardState.hasChangedDesignSetting) {
      this.dashboardApi.updateChartDesign(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        this.dashboardState.getSelectedChartValue()).subscribe(
          () => {
            if(!shouldDisableSaveAlert){
              this.updateShouldSaveDashboard();
            }
            this.dashboardState.hasChangedDesignSetting = false;
          },
          (errorRes) => {
            this.dashboardState.rollBackChartDesignSetting();
            this.toastr.showError(errorRes.error.message);
            this.dashboardState.hasChangedDesignSetting = false;
          },
        );
    }
  }
  updateChartAnnotations(shouldDisableSaveAlert?:boolean): void {
    if (this.dashboardState.getSelectedChartValue() && this.dashboardState.getRollbackChartAnnotationSettingValue() &&
      this.dashboardState.hasChangedDesignSetting) {
      this.dashboardApi.updateChartAnnotation(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        this.dashboardState.getSelectedChartValue()).subscribe(
          () => {
            if(!shouldDisableSaveAlert){
              this.updateShouldSaveDashboard();
            }
            this.dashboardState.hasChangedDesignSetting = false;
          },
          (errorRes) => {
            this.dashboardState.rollBackChartAnnotationSetting();
            this.toastr.showError(errorRes.error.message);
            this.dashboardState.hasChangedDesignSetting = false;
          },
        );
    }
  }

  updateChartTotalRow(chart: Partial<ChartModel>) {
    this.dashboardApi.updateChartTotalRow(chart, this.dashboardState.workspaceId, this.dashboardState.dashboardId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        chart.chartDesignSettings.chart.totals = chart.hasTotalRow;
        this.setChartDesign(chart);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        chart.hasTotalRow = !chart.hasTotalRow;
        this.toastr.showError(errorRes.error.message);
      },
    );
  }
  updateIsPaginated(chart: Partial<ChartModel>): void {
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
  }
  createChart(chart: Partial<ChartModel>, shouldDisableSaveAlert?:boolean) {
    this.dashboardState.isAddNewChartInProgress.set(true);
    this.dashboardApi.createChart(this.dashboardState.workspaceId,this.dashboardState.dashboardId, chart).subscribe(
      (res) => {
        if(!shouldDisableSaveAlert){
          this.updateShouldSaveDashboard();
        }
        chart.chartId = res.data.chartId;
        chart.name = res.data.name;
        if(chart.chartType === 'ShapeMap' || chart.chartType === 'GeoMapChart' || chart.chartType === 'BubbleChart'){
          this.dashboardApi.updateMapType(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
              chart.chartId,{});
        }
        this.loadChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart.chartId);
        setTimeout(()=>{
          this.dashboardState.isAddNewChartInProgress.set(false);
        }, 1000)
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.dashboardState.isAddNewChartInProgress.set(false);
      },
      () => {

      },
    );
  }
  newChart$(): Observable<any> {
    return this.dashboardState.getNewChart$();
  }

  refreshSelectedChartFiltersMissingState(): void {
    this.dashboardState.refreshSelectedChartFiltersMissingState();
  }

  //dashboards list
  loadAssetsOfWorkspace(workspaceId: string, dashboardId: string, sortOrder: SortOrder): void {
    this.dashboardState.dashboardListSetUpdating(true);
    this.dashboardState.setDashboardListIsLoading(true);
    this.dashboardApi.getAssetOfWorkspace(workspaceId, sortOrder).subscribe(
      (res) => {
        this.dashboardState.updateAssetList(res.data, dashboardId);
      },
      () => {
        this.toastr.showError('خطا در دریافت لیست داشبوردها');
      },
      () => {
        this.dashboardState.dashboardListSetUpdating(false);
        this.dashboardState.setDashboardListIsLoading(false);
      },
    );
  }

  getAssetsOfWorkspace(): Observable<Partial<DashboardAndGroupInList>[]> {
    return this.dashboardState.getAssetsList$();
  }

  getDashboardListIsLoading$(): Observable<boolean> {
    return this.dashboardState.getDashboardListIsLoading$();
  }

  //settings sidebar
  dashboardSettingSidebarCompacted$(): Observable<boolean> {
    return this.dashboardState.dashboardSettingIsCompacted$();
  }
  dashboardSettingSidebarToggleCompact(compacted: boolean) {
    this.dashboardState.dashboardSettingSidebarToggleCompact(compacted);
  }

  setDashboardSettingChangeSizeEvent(event: void) {
    this.dashboardState.setDashboardSettingChangeSizeEvent(event);
  }

  getDashboardSettingChangeSizeEvent$(): Observable<void> {
    return this.dashboardState.getDashboardSettingChangeSizeEvent$();
  }

  getSettingSidebarId$(): Observable<ChartType> {
    return this.dashboardState.getSettingSidebarId$();
  }

  setSettingSidebarId(settingSidebarId: ChartType) {
    this.dashboardState.setSettingSidebarId(settingSidebarId);
  }

  getSelectedChart$(): Observable<Partial<ChartModel>> {
    return this.dashboardState.getSelectedChart$();
  }

  updateColorSet(colorSet: ColorSet) {
    this.colorService.setColorSet(colorSet);
  }

  updateSelectedChart(chart: Partial<ChartModel>): void {
    const currentSelectedChart = this.dashboardState.getSelectedChartValue();
    if(currentSelectedChart?.chartId !== chart?.chartId || !chart) {
      this.dashboardState.updateSelectedChartFilters(undefined);
    }
    this.dashboardState.setSelectedChart(chart);
  }

  openChartSetting(chart: any) {
    this.setSettingSidebarId(chart.type);
    this.updateSelectedChart(chart.id);
  }

  dashboardIsFullSize(): Observable<boolean> {
    return this.dashboardState.dashboardIsFullSize();
  }

  getDashboardId(){
    return this.dashboardState.dashboardId
  }

  dashboardToggleFullSize(fullSize = undefined) {
    this.dashboardState.toggleDashboardFullSize(fullSize);
  }

  chartIsFullSize(): Observable<boolean> {
    return this.dashboardState.chartIsFullSize();
  }

  chartToggleFullSize(fullSize) {
    this.dashboardState.toggleChartFullSize(fullSize);
  }

  deleteChart(chartId: string) {
    this.dashboardApi.deleteChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteChart(chartId);
        this.deleteGridItem(chartId);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteGridItem(gridId: string) {
    const dashboardTemp = this.dashboardState.deleteGridItem(gridId);
    this.updateDashboard(dashboardTemp);
  }
  copyChart(chart: Partial<ChartModel>, gridItem: GridItem) {
      this.updateChartDesign();
      this.updateChartAnnotations();
      this.dashboardState.isCopyChartInProgress.set(true);
      this.dashboardApi.duplicateChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart.chartId)
          .subscribe(
          (duplicateChart) => {
              this.updateShouldSaveDashboard();
              this.dashboardApi.getChart(this.dashboardState.workspaceId,
                  this.dashboardState.dashboardId, duplicateChart.data.chartId).subscribe(
                  (response) => {
                      let newChart = response.data;
                      gridItem.id = newChart.chartId;
                      newChart.controllersValues = chart.controllersValues;
                      newChart.chartDesignSettings = this.jsonAdapterService
                          .jsonify<ChartDesignSettings>(this.jsonAdapterService.stringify(chart.chartDesignSettings));
                      newChart = this.chartPermissionService.addPermissionToEntity(newChart,
                          newChart.permissions, this.userService.getUserMinInfoValue(), false);
                      this.dashboardState.copyChart(newChart);
                      this.copyGridItem(gridItem);
                      this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, newChart);
                      this.dashboardState.setInitialChartsDrillDownMode(this.dashboardState.getRollBackDashboardTemp().charts.length);
                      setTimeout(()=>{
                        this.dashboardState.isCopyChartInProgress.set(false);
                      }, 1000)
                  },
                  (errorRes) => {
                      this.dashboardState.isCopyChartInProgress.set(false);
                      this.toastr.showError(errorRes.error.message);
                  },
              );
          },
    );

  }

  copyGridItem(gridItem: GridItem) {
    const dashboardTemp = this.dashboardState.copyGridItem(gridItem);
    this.updateDashboard(dashboardTemp, false,false,()=>{
      // this.dashboardState.updateDashboard(dashboardTemp);
    }, undefined, true);
  }

  //filters
  getDashboardFilters$(): Observable<Partial<Filter>[]> {
    return this.dashboardState.getDashboardFilters$();
  }

  loadDashboardFilters(workspaceId: string, dashboardId: string): void {
    this.dashboardApi.getDashboardFilters$(workspaceId, dashboardId).subscribe(
      (filtersResponse) => {
        this.dashboardState.updateDashboardFilters(filtersResponse.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  createDashboardFilter(filter: Filter, dashboard: Dashboard, columnType: ColumnType) {
    switch (filter.filterType) {
      case FilterType.Basic:
        this.dashboardApi.createDashboardBasicFilter(filter, this.dashboardState.workspaceId,
            this.dashboardState.dashboardId).subscribe(
          (response) => {
            this.updateShouldSaveDashboard();
            filter.columnType = columnType;
            this.dashboardState.createDashboardFilter(filter, response.data.filterId);
            this.loadDashboardCharts(dashboard);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
      case FilterType.AdvancedQuality:
        this.dashboardApi.updateDashboardAdvancedQualityFilter(filter, this.dashboardState.workspaceId,
            this.dashboardState.dashboardId).subscribe(
          (response) => {
            this.updateShouldSaveDashboard();
            this.dashboardState.createDashboardFilter(filter, response.data);
            this.loadDashboardCharts(dashboard);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
      case FilterType.AdvancedQuantity:
        this.dashboardApi.updateDashboardAdvancedQuantityFilter(filter, this.dashboardState.workspaceId,
            this.dashboardState.dashboardId).subscribe(
          (response) => {
            this.updateShouldSaveDashboard();
            this.dashboardState.createDashboardFilter(filter, response.data);
            this.loadDashboardCharts(dashboard);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
    }
  }

  updateDashboardFilters(filter: Partial<Filter>, dashboard: Dashboard) {
    switch (filter.filterType) {
      case FilterType.Basic:
        this.dashboardApi.updateDashboardBasicFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId).subscribe(
            (res) => {
              this.updateDashboardFilter(filter, res.data.filterId, dashboard);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.AdvancedQuality:
        this.dashboardApi.updateDashboardAdvancedQualityFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId).subscribe(
            (res) => {
              this.updateDashboardFilter(filter, res.data.filterId, dashboard);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.AdvancedQuantity:
        this.dashboardApi.updateDashboardAdvancedQuantityFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId).subscribe(
            (res) => {
              this.updateDashboardFilter(filter, res.data.filterId, dashboard);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.Date:
        this.dashboardApi.updateDashboardDateFilter(filter).subscribe(
          (res) => {
            this.updateDashboardFilter(filter, res.data.filterId, dashboard);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
      case FilterType.DateList:
        this.dashboardApi.updateDashboardDateListFilter(filter).subscribe(
          (res) => {
            this.updateDashboardFilter(filter, res.data.filterId, dashboard);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
    }
  }

  updateDashboardFilter(filter: Partial<Filter>, newFilterId: string, dashboard: Dashboard): void {
    filter.filterId = newFilterId;
    this.updateShouldSaveDashboard();
    this.dashboardState.updateSingleDashboardFilter(filter);
    this.dashboardState.updateFilterSubmitting(Submit.submitted);
    this.loadDashboardCharts(dashboard);
  }

  deleteDashboardFilter(filter: Partial<Filter>, dashboard: Dashboard) {
    this.dashboardApi.deleteDashboardFilter(
      this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,
      filter.filterId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteDashboardFilter(filter);
        this.loadDashboardCharts(dashboard);
      },
    );
  }

  getSelectedChartFilter$(): Observable<Partial<Filter>[]> {
    return this.dashboardState.getSelectedChartFilters$();
  }

  loadSelectedChartFilter(chartId: string): void {
      this.dashboardApi.getChartFilter(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chartId).subscribe(
          (response) => {
              if (this.dashboardState.getSelectedChartValue() &&
                  this.dashboardState.getSelectedChartValue().chartId === chartId) {
                  this.dashboardState.updateSelectedChartFilters(response.data);
              }
          },
      );
  }


  createChartFilter(filter: Filter, chart: Partial<ChartModel>, columnType: ColumnType): void {
    chart.isLoading = true;
    if (filter.filterType === FilterType.Basic) {
      this.dashboardApi.createChartBasicFilter(filter,this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
        (response) => {
          this.updateShouldSaveDashboard();
          filter.columnType = columnType;
          this.dashboardState.createChartFilter(filter, response.data.filterId);
          this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
      );
    }
  }

  deleteChartFilter(chart: Partial<ChartModel>, filterId: string): void {
    chart.isLoading = true;
    this.dashboardApi.deleteChartFilter(chart.chartId,this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, filterId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteChartFilter(filterId);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateChartFilters(filter: Partial<Filter>, chart: Partial<ChartModel>): void {
    switch (filter.filterType) {
      case FilterType.Basic:
        this.dashboardApi.updateChartBasicFilter(filter, this.dashboardState.workspaceId, this.dashboardState.dashboardId,
          chart.chartId).subscribe(
            (res) => {
              this.updateChartFilter(filter, res.data.filterId, chart);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.AdvancedQuality:
        this.dashboardApi.updateChartAdvancedQualityFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
            (res) => {
              this.updateChartFilter(filter, res.data.filterId, chart);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.AdvancedQuantity:
        this.dashboardApi.updateChartAdvancedQuantityFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
            (res) => {
              this.updateChartFilter(filter, res.data.filterId, chart);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.TopNQuality:
        this.dashboardApi.updateChartTopNQualityFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
            (res) => {
              this.updateChartFilter(filter, res.data.filterId, chart);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.TopNQuantity:
        this.dashboardApi.updateChartTopNQuantityFilter(filter, this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
            (res) => {
              this.updateChartFilter(filter, res.data.filterId, chart);
            },
            (errorRes) => {
              this.toastr.showError(errorRes.error.message);
            },
          );
        break;
      case FilterType.Date:
        this.dashboardApi.updateChartDateFilter(filter, chart.chartId).subscribe(
          (res) => {
            this.updateChartFilter(filter, res.data.filterId, chart);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
      case FilterType.DateList:
        this.dashboardApi.updateChartDateListFilter(filter, chart.chartId).subscribe(
          (res) => {
            this.updateChartFilter(filter, res.data.filterId, chart);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
        break;
    }
  }

  updateChartFilter(filter: Partial<Filter>, newFilterId: string, chart: Partial<ChartModel>): void {
    this.updateShouldSaveDashboard();
    filter.filterId = newFilterId;
    this.dashboardState.updateSingleSelectedChartFilter(filter);
    this.dashboardState.updateFilterSubmitting(Submit.submitted);
    this.loadChartData(chart.workspaceId, chart.dashboardId, chart);
  }

  addChartSort(chart: Partial<ChartModel>, sortColumn: Partial<ChartInteractiveSort>, columnType: ColumnType, index: number): void {
    chart.isLoading = true;
    this.dashboardApi.addChartSort(this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart.chartId, sortColumn).subscribe(
      (res) => {
        sortColumn.id = res.data.id;
        sortColumn.isAscending = res.data.isAscending;
        sortColumn.priority = res.data.priority;
        this.updateShouldSaveDashboard();
        this.dashboardState.addChartSort(sortColumn, chart.chartId, index);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        chart.isLoading = false;
      },
    );
  }
  updateSortColumn(newColumn: Partial<ChartInteractiveSort>, chart: Partial<ChartModel>): void {
    this.dashboardApi.updateChartSort(
      this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,
      chart.chartId,
      newColumn
    ).subscribe(
      (res: any) => {
        const status: SortError = res?.data?.priorityStatus;
        switch (status) {
          case SortError.NumberOfColumnsNotEqual:
            this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
            break;
          case SortError.PrioritiesNotEqual:
            this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
            break;
        }
        this.updateShouldSaveDashboard();
        this.dashboardState.updatSortColumn(chart.chartId, newColumn.id, newColumn);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }
//   updateChartSort(chart: Partial<ChartModel>, sortColumn: Partial<ChartInteractiveSort>): void {
//     this.dashboardApi.updateChartSort(
//         this.dashboardState.workspaceId,
//         this.dashboardState.dashboardId,
//         chart.chartId,
//         sortColumn
//     ).subscribe(
//       (res) => {
//         this.updateShouldSaveDashboard();
//         const updatedSorts = chart.chartDataSettings.sortColumns.map(sort =>
//           sort.columnId === sortColumn.columnId ? sortColumn : sort
//         );
//         chart.chartDataSettings.sortColumns = updatedSorts;
//         this.dashboardState.updateOneChart(chart);
//         this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
//       },
//       (errorRes) => {
//         this.toastr.showError(errorRes.error.message);
//       },
//     );
// }

  getFilterSubmitting$(): Observable<Submit> {
    return this.dashboardState.getFilterSubmitting$();
  }

  updateFilterSubmitting(submitting: Submit) {
    this.dashboardState.updateFilterSubmitting(submitting);
  }

  createColumnSortPerDashboard(column: Partial<DashboardColumn>, dataSourceId: string) {
    this.dashboardApi.createColumnSortPerDashboard(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, column, dataSourceId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        column.hasSort = true;
        this.dashboardState.updateDataSourceColumnInDashboard(column, dataSourceId);
        this.loadDashboardCharts(this.dashboardState.getDashboardValue());
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateColumnSortPerDashboard(column: Partial<DashboardColumn>, dataSourceId: string) {
    this.dashboardApi.updateColumnSortPerDashboard(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, column, dataSourceId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.loadDashboardCharts(this.dashboardState.getDashboardValue());
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteColumnSortPerDashboard(column: Partial<DashboardColumn>, dataSourceId: string) {
    this.dashboardApi.deleteColumnSortPerDashboard(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, column, dataSourceId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        column.hasSort = false;
        column.columnSortType = 'None';
        column.baseColumn = undefined;
        this.dashboardState.updateDataSourceColumnInDashboard(column, dataSourceId);
        this.loadDashboardCharts(this.dashboardState.getDashboardValue());
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  // controller
  calculateDateController(controller: Partial<Controller>): void {
      if(controller.controllerValue.dateControllerType !== 'DateRange' &&
          this.dashboardState.validationDateController(controller.controllerValue)) {
          this.dashboardApi.calculateDateController(controller, this.dashboardState.workspaceId,
              this.dashboardState.dashboardId).subscribe(
              (response) => {
                  if(Array.isArray(response.data)) {
                      controller.controllerValue.startDate = response.data[0];
                      controller.controllerValue.endDate = response.data[1];
                  }
                  this.addControllerToDashboardCharts(controller);
              });
      } else {
          this.addControllerToDashboardCharts(controller);
      }
  }

  calculateDateListController(controller: Partial<Controller>): void {
    this.dashboardApi.calculateDateListController(controller, this.dashboardState.workspaceId,
      this.dashboardState.dashboardId).subscribe(
        (response) => {
          if (Array.isArray(response.data)) {
            controller.controllerValue.startDate = response.data[0];
            controller.controllerValue.endDate = response.data[1];
          }
          this.addControllerToDashboardCharts(controller);
        });
  }

  addControllerToDashboardCharts(controller: Partial<Controller>): void {
      this.updateControllersState(controller);
      this.loadChartsDataOnUpdateControllersValue(this.dashboardState.getDashboardValue().charts,
          this.dashboardState.workspaceId,
          this.dashboardState.dashboardId);
  }

  deleteController(controllerId: string): void {
    this.dashboardApi.deleteChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId, controllerId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteChart(controllerId);
        this.deleteGridItem(controllerId);
        this.deleteControllerValue(controllerId);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteControllerValue(controllerIds: string | string[], cb?:any, callerType?:string): void {
    const idsToDelete = Array.isArray(controllerIds) ? controllerIds : [controllerIds];
    idsToDelete.forEach(controllerId => {
        this.dashboardState.deleteChartsControllerValue(controllerId);
    });
    if(cb){
      cb()
    }
    this.loadChartsDataOnUpdateControllersValue(
        this.dashboardState.getDashboardValue().charts,
        this.dashboardState.workspaceId,
        this.dashboardState.dashboardId,
       callerType
    );
  }

  updateControllersState(controller: Partial<Controller>): void {
      this.dashboardState.updateChartsController(controller);
      this.dashboardState.updateControllersValuesState(controller);
  }

  getControllers$(): Observable<Partial<Controller>[]> {
    return this.dashboardState.getControllers$();
  }

  getControllersValue(): Partial<Controller>[] {
    return this.dashboardState.getControllersValue();
  }

  rollBackChartsControllersValues(): void {
    this.dashboardState.rollBackChartsControllersValues();
  }

  updateControllerDefaultValues(chart: Partial<ChartModel>): void {
    if (chart.chartType === 'DateListController') {
      this.dashboardApi.updateDefaultValueForDateListControllers(this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart).subscribe(
          (response) => {
            this.updateShouldSaveDashboard();
            chart.defaultValuesForController.defaultValues = response.data;
            this.dashboardState.updateDefaultControllerValues(chart);
            this.dashboardState.updateOneChart(chart);
            this.loadChartsDataOnUpdateControllersValue(this.dashboardState.getDashboardValue().charts,
              this.dashboardState.workspaceId,
              this.dashboardState.dashboardId);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          });
    } else {
      if (chart.defaultValuesForController.defaultValueType !== 'DateRange' &&
        chart.chartType === 'DateRangeController') {
        chart.defaultValuesForController.defaultValues = [];
      }
      this.dashboardApi.updateControllerDefaultValues(this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart).subscribe(
          (response) => {
            this.updateShouldSaveDashboard();
            if (chart.defaultValuesForController.defaultValueType !== 'DateRange' &&
              chart.chartType === 'DateRangeController') {
              chart.defaultValuesForController.defaultValues = response.data;
              console.log("chart.defaultValuesForController.defaultValues:;",chart.defaultValuesForController.defaultValues)
            }
            this.dashboardState.updateDefaultControllerValues(chart);
            this.dashboardState.updateOneChart(chart);
            this.loadChartsDataOnUpdateControllersValue(this.dashboardState.getDashboardValue().charts,
              this.dashboardState.workspaceId,
              this.dashboardState.dashboardId);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
    }
  }

  updateControllerHasDefaultValue(chart: Partial<ChartModel>): void {
      this.dashboardApi.updateControllerHasDefaultValue(this.dashboardState.workspaceId,
          this.dashboardState.dashboardId,chart).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              const relativeRange = chart.chartDesignSettings.chart.dateControllerType === 'Relative'
              const advancedSingle = chart.chartDesignSettings.chart.dateControllerType === 'Advance' &&
                  chart.chartDesignSettings.chart.singleSelect;
              const advancedRange = chart.chartDesignSettings.chart.dateControllerType === 'Advance' &&
                  !chart.chartDesignSettings.chart.singleSelect;
              const controllerValue = this.dashboardState.selectedControllerValue.controllerValue;
              const hasDateRangeController = controllerValue?.controllerType === 'DataRangeController' &&
                  (controllerValue?.dateControllerType === 'DateRange' && controllerValue?.startDate);
              const hasAdvancedSingleDateController = controllerValue?.controllerType === 'DataRangeController' &&
                  (advancedSingle && (((controllerValue.advancedSingleDate.rangeParameterDate === 'BeforeFrom' ||
                              controllerValue.advancedSingleDate.rangeParameterDate === 'AfterFrom') &&
                          controllerValue.advancedSingleDate.date != null) ||
                      controllerValue.advancedSingleDate.rangeParameterDate === 'Before' ||
                      controllerValue.advancedSingleDate.rangeParameterDate === 'After'));
              const hasRelativeRangeDateController =
                  controllerValue?.controllerType === 'DataRangeController' &&
                  (relativeRange && (controllerValue.relativeRangeDate.scopeValue !== null || (controllerValue.relativeRangeDate.scopeValue === null && controllerValue.relativeRangeDate.timeMode == "Present" )));
              const hasAdvanceRangeDateController =
                  controllerValue?.controllerType === 'DataRangeController' &&
                  (advancedRange && (controllerValue.advancedRangeDate?.secondParameterDate === 'Current' ||
                          (controllerValue.advancedRangeDate?.secondParameterDate === 'DateRange' &&
                              controllerValue.advancedRangeDate.timeScopeValue !== null &&
                              controllerValue.advancedRangeDate.secondParameterDate != null)));
              const hasDropDownController =
                  controllerValue?.controllerType === 'DropdownController' &&
                  controllerValue?.selectedValues?.length > 0;
              const hasDateListController =
                  controllerValue?.controllerType === 'DateListController' &&
                  controllerValue?.selectedValues?.length > 0;
              if(chart.defaultValuesForController.defaultValues.length > 0 &&
                chart.defaultValuesForController.hasDefaultValue && !hasDropDownController && !hasDateListController &&
                  !(hasDateRangeController || hasAdvancedSingleDateController || hasAdvanceRangeDateController || hasRelativeRangeDateController)) {
                    this.dashboardState.updateDefaultControllerValues(chart);
                  this.loadChartsDataOnUpdateControllersValue(this.dashboardState.getDashboardValue().charts,
                      this.dashboardState.workspaceId,
                      this.dashboardState.dashboardId);
              }
              this.dashboardState.updateOneChart(chart);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  resetControllerDefaultValues(controller: Partial<ChartModel>): void {
      if (this.shouldResetDefaultValuesForDateRangeController(controller)) {
          controller.defaultValuesForController.defaultValues = [];
          controller.defaultValuesForController.defaultValueType = 'DateRange';
          this.updateControllerDefaultValues(controller);
      }
      if (this.shouldResetDefaultValuesForAdvanceSingleDateController(controller)) {
          controller.defaultValuesForController.defaultValueAdvancedSingle = null;
          controller.defaultValuesForController.defaultValueType = 'AdvancedSingle';
          this.updateControllerDefaultValues(controller);
      }
      if (this.shouldResetDefaultValuesForAdvanceRangeDateController(controller)) {
          controller.defaultValuesForController.defaultValueAdvancedRange = null;
          controller.defaultValuesForController.defaultValueType = 'AdvancedRange';
          this.updateControllerDefaultValues(controller);
      }
      if (this.shouldResetDefaultValuesForRelativeDateController(controller)) {
        controller.defaultValuesForController.defaultValueRelative = {
          parameterDate: "TodayDate",
          parameterDateValue:null,
          scope: "Day",
          scopeValue: null,
          timeMode: "Past",
        };
        controller.defaultValuesForController.defaultValueType = 'Relative';
        this.updateControllerDefaultValues(controller);
    }
  }

  shouldResetDefaultValuesForDateRangeController(controller: Partial<ChartModel>): boolean {
    return controller.chartDesignSettings.chart.dateControllerType === 'Calendar' &&
      controller.defaultValuesForController.defaultValueType === 'DateRange' &&
      controller.defaultValuesForController.defaultValues.length > 0;
  }

  shouldResetDefaultValuesForAdvanceSingleDateController(controller: Partial<ChartModel>): boolean {
      const defaultValueAdvancedSingle = controller.defaultValuesForController.defaultValueAdvancedSingle;
      return controller.chartDesignSettings.chart.dateControllerType === 'Advance' &&
          controller.chartDesignSettings.chart.singleSelect &&
          defaultValueAdvancedSingle != null && (defaultValueAdvancedSingle.date != null ||
              defaultValueAdvancedSingle.timeScopeValue != null ||
              defaultValueAdvancedSingle.rangeParameterDate != null ||
              defaultValueAdvancedSingle.timeScope != null);
  }

    shouldResetDefaultValuesForAdvanceRangeDateController(controller: Partial<ChartModel>): boolean {
        const defaultValueAdvancedRange = controller.defaultValuesForController.defaultValueAdvancedRange;
        return controller.chartDesignSettings.chart.dateControllerType === 'Advance' &&
            !controller.chartDesignSettings.chart.singleSelect  &&
            defaultValueAdvancedRange != null &&
            (defaultValueAdvancedRange.firstTimeScope != null ||
                defaultValueAdvancedRange.secondTimeScope != null ||
                defaultValueAdvancedRange.firstParameterDate != null ||
                defaultValueAdvancedRange.secondParameterDate != null ||
                defaultValueAdvancedRange.timeScopeValue != null);
    }
    shouldResetDefaultValuesForRelativeDateController(controller: Partial<ChartModel>): boolean {
      const defaultValueRelative = controller.defaultValuesForController.defaultValueRelative;
      return controller.chartDesignSettings.chart.dateControllerType === 'Calendar' &&
        defaultValueRelative != null &&
        (
          defaultValueRelative.parameterDate != "TodayDate" ||
          defaultValueRelative.parameterDateValue != null ||
          defaultValueRelative.scope != "Day" ||
          defaultValueRelative.scopeValue != null ||
          defaultValueRelative.timeMode != "Past"
        );
    }


  // comment
  loadComments(workspaceId: string, assetId: string, commentType: CommentType): void {
    this.dashboardState.setCommentsType(commentType);
    this.dashboardApi.getComments(workspaceId, assetId, commentType).pipe(map(res => res.data)).subscribe(
      (comments: Comment[]) => {
        this.dashboardState.updateComments(comments);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  postComment(newComment: any): void {
    this.setSubmitState('submitting');
    newComment = this.dashboardState.findMentionedIds(newComment);
    this.dashboardApi.postComment(newComment).pipe(map(res => res.data)).subscribe(
      (comment: Comment) => {
        this.addCreatorNameToComment(comment);
        this.setSubmitState('submitted');
        this.toastr.showSuccess('نظر ثبت شد.');
      },
      (errorRes) => {
        this.setSubmitState('submitted');
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  editComment(editComment: any, comment: Comment): void {
    this.setSubmitState('submitting');
    editComment = this.dashboardState.findMentionedIds(editComment);
    this.dashboardApi.editComment(editComment).subscribe(
      () => {
        const newComment = {...comment};
        newComment.comment = editComment.comment;
        newComment.mentionedUserIds = editComment.mentionedUserIds;
        this.dashboardState.updateComment(newComment, newComment.isResolved);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.setSubmitState('submitted');
      },
    );
  }

  resolveComment(workspaceId: string, assetId: string, comment: Comment): void {
    this.setSubmitState('submitting');
    this.dashboardApi.resolveComment(workspaceId, assetId, comment.id).subscribe(
      () => {
        this.dashboardState.updateComment(comment, true);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.setSubmitState('submitted');
      },
    );
  }

  unResolveComment(workspaceId: string, assetId: string, comment: Comment): void {
    this.setSubmitState('submitting');
    this.dashboardApi.unResolveComment(workspaceId, assetId, comment.id).subscribe(
      () => {
        this.dashboardState.updateComment(comment, false);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.setSubmitState('submitted');
      },
    );
  }

  addCreatorNameToComment(newComment: Comment): void {
    const user = this.userService.getUserMinInfoValue();
    newComment.creatorName = user.firstName + ' ' + user.lastName;
    this.dashboardState.addComment(newComment);
  }

  getComments$(): Observable<Comment[]> {
    return this.dashboardState.getComments$();
  }

  toggleDashboardComments(commentsDisplayed: boolean): void {
    this.dashboardState.updateToggleDashboardComments(commentsDisplayed);
  }

  dashboardCommentsDisplayed$(): Observable<boolean> {
    return this.dashboardState.dashboardCommentsDisplayed$();
  }

  loadUsersToMention(): void {
    this.dashboardApi.getUsersToMention(this.dashboardState.workspaceId, this.dashboardState.dashboardId)
        .pipe(map(res => res.data.users)).subscribe(
      (users) => {
        this.dashboardState.setUsersToMention(users);
        this.loadComments(this.dashboardState.workspaceId, this.dashboardState.dashboardId, 'All');
      },
    );
  }

  setCommentState(state: CommentState): void {
    this.dashboardState.setCommentState(state);
  }

  getCommentState$(): Observable<CommentState> {
    return this.dashboardState.getCommentState$();
  }

  resetComments(): void {
    this.dashboardState.resetComments();
  }
  resetInsights(){
    this.dashboardState.resetInsights();
  }
  // colors


  updateColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    switch (columnColor.chartColumnType) {
      case 'Descriptor':
        this.updateDescriptorColumnColor(columnColor,chart);
        break;
      case 'Legend':
        this.updateLegendColumnColor(columnColor,chart);
        break;
      case 'Value':
        this.updateValueColumnColor(columnColor,chart);
        break;
      case 'SecondValue':
        this.updateSecondValueColumnColor(columnColor,chart);
        break;
      case 'SourceGroup':
        this.updateSourceGroupColumnColor(columnColor, chart);
        break;
      case 'TargetGroup':
        this.updateTargetGroupColumnColor(columnColor, chart);
        break;
    }
  }

  updateDescriptorColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    this.dashboardState.updateChartColors(chart, columnColor);
    this.dashboardApi.updateDescriptorColumnColor(columnColor,this.dashboardState.dashboardId,
      this.dashboardState.workspaceId)
      .subscribe(
        () => {
            this.updateShouldSaveDashboard();
        },
        (errorRes) => {
          this.dashboardState.rollBackChartColors();
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  updateLegendColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    this.dashboardState.updateChartColors(chart, columnColor);
    this.dashboardApi.updateLegendColumnColor(columnColor,this.dashboardState.dashboardId,this.dashboardState.workspaceId)
      .subscribe(
        () => {
            this.updateShouldSaveDashboard();
        },
        (errorRes) => {
          this.dashboardState.rollBackChartColors();
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  updateValueColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    this.dashboardState.updateChartColors(chart, columnColor);
    this.dashboardApi.updateValueColumnColor(columnColor,
      this.dashboardState.dashboardId,this.dashboardState.workspaceId)
      .subscribe(
        () => {
            this.updateShouldSaveDashboard();
        },
        (errorRes) => {
          this.dashboardState.rollBackChartColors();
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  updateSecondValueColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    this.dashboardState.updateChartColors(chart, columnColor);
    this.dashboardApi.updateSecondValueColumnColor(columnColor,
      this.dashboardState.dashboardId,this.dashboardState.workspaceId)
      .subscribe(
        () => {
            this.updateShouldSaveDashboard();
        },
        (errorRes) => {
          this.dashboardState.rollBackChartColors();
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  updateSourceGroupColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    this.dashboardState.updateChartColors(chart, columnColor);
    this.dashboardApi.updateSourceGroupColumnColor(columnColor,this.dashboardState.dashboardId,
      this.dashboardState.workspaceId)
      .subscribe(
        () => {
            this.updateShouldSaveDashboard();
        },
        (errorRes) => {
          this.dashboardState.rollBackChartColors();
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  updateTargetGroupColumnColor(columnColor: Partial<ColumnColor>, chart: Partial<ChartModel>): void {
    this.dashboardState.updateChartColors(chart, columnColor);
    this.dashboardApi.updateTargetGroupColumnColor(columnColor,this.dashboardState.dashboardId,
      this.dashboardState.workspaceId)
      .subscribe(
        () => {
            this.updateShouldSaveDashboard();
        },
        (errorRes) => {
          this.dashboardState.rollBackChartColors();
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  updateChartColorsTheme(chart: Partial<ChartModel>, colorSetId: number): void {
    this.dashboardApi.resetChartColors(this.dashboardState.workspaceId,this.dashboardState.dashboardId,
      chart.chartId,colorSetId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        chart.colorSetId = colorSetId;
        this.loadChartData(this.dashboardState.workspaceId,this.dashboardState.dashboardId,chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  //drill through
  loadDashboardDrillThroughFilters(workspaceId: string, dashboardId: string): void {
      if(this.dashboardState.drillThroughFiltersState === 'GetDrillThroughFiltersDestinationDashboard') {
          this.loadDrillThroughFilters(workspaceId, dashboardId, true);
      }
      if(this.dashboardState.drillThroughFiltersState === 'GetDrillThroughFiltersSourceDashboard') {
          this.loadDrillThroughFilters(workspaceId, dashboardId, false);
      }
  }

  loadDrillThroughFilters(workspaceId: string, dashboardId: string, isDestinationDashboard: boolean): void {
      this.dashboardApi.getDrillThroughFilters(workspaceId, dashboardId,isDestinationDashboard).subscribe(
          (res) => {
              let filters = [];
              if(res?.data) {
                  if(!isDestinationDashboard) {
                      this.dashboardState.deleteDrillThroughSourceDashboard();
                  }
                  filters = res.data.controllersValueForDrillThrough;
              }else{
                this.dashboardState.deleteDrillThroughSourceDashboard();
              }
              this.dashboardState.setDrillThroughFilters(filters,isDestinationDashboard);
              this.dashboardState.setDrillThroughChartFilters(res?.data?.chartFilters || []);
          },
      );
  }

  clearDrillThroughChartFilters():void {
    this.dashboardState.setDrillThroughChartFilters([]);
  }

  loadDashboardsDrillThrough(chartId: string): void {
    this.dashboardApi.getDashboardsForDrillThroughFromChart(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,chartId).subscribe(
      (res) => {
        this.dashboardState.updateDashboardsForDrillThrough(res.data.dashboards);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadDashboardUsedInDrillThrough(chartId: string): void {
    this.dashboardApi.getDashboardUsedInDrillThroughName(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,chartId).subscribe(
      (res) => {
        this.dashboardState.updateDashboardUsedInDrillThrough(res?.data);
      },
    );
  }

  resetDashboardsDrillThroughOnSelectedChart(chart: Partial<ChartModel>): void {
    this.dashboardState.updateDashboardsForDrillThrough(undefined);
    if(chart.interactionWithPagesProperties.isInteractableWithPages &&
      chart.interactionWithPagesProperties.interactionWithPagesType === 'ThroughDashboard') {
      this.loadDashboardsDrillThrough(chart.chartId);
      this.loadDashboardUsedInDrillThrough(chart.chartId);
    }
  }

  // defaults
  setSubmitState(state: SubmitType): void {
    this.dashboardState.setSubmitState(state);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.dashboardState.getSubmitState$();
  }
  setDashboardChartNameSubmitState(state: SubmitType): void {
    this.dashboardState.setDashboardChartNameSubmitState(state);
  }

  getDashboardChartNameSubmitState$(): Observable<SubmitType> {
    return this.dashboardState.getDashboardChartNameSubmitState$();
  }


  setGridMigrationState(isMigrated: boolean): void {
    this.dashboardState.setGridMigrated(isMigrated);
  }

  getGridMigrationState$(): Observable<boolean> {
    return this.dashboardState.getGridMigrationState$();
  }

  resetStates(): void {
    this.dashboardState.resetStates();
  }

  getDataColumnIcons(data) {
    if(data?.parameterProperty) {
      if (data?.columnIsMissed){
          return 'warning';
      }
      return this.getDataColumnIconForParameter(data as DashboardColumn);
    } else {
      if (data?.columnIsMissed){
        return 'warning';
      } else if (data?.filterId && data?.columnDataType) {
        return this.getDataColumnIconForFilter(data as Partial<Filter>);
      } else if(data?.columnId && data?.dataType) {
        return this.getDataColumnIconForDashboardColumn(data as DashboardColumn);
      }else if(data?.isAscending) {
        return this.getDataColumnIconForDescriptor(data as Partial<DescriptorColumn>);
      }else if(data?.formatShow && data?.dataType) {
        return this.getDataColumnIconForLegend(data as Partial<LegendColumn>);
      }else if(data?.priority && data?.columnAggregationType){
        return this.getDataColumnIconForValue(data as Partial<ValueColumn>);
      }else if(data?.hasSort) {
        return this.getDataColumnIconForSort(data as Partial<ChartInteractiveSort>);
      } else {
        return this.getIconBaseOnColumnType(data?.columnType?.toString(),
          data?.dataModuleType ?? data?.columnDataModuleType) ??
          this.getIconBaseOnDataType(data?.columnDataType?.toString());
      }
    }
  }

  getDataColumnIconForParameter(data: DashboardColumn): string {
    return this.getIconParameterOnColumnType(data?.columnType.toString());
  }

  getDataColumnIconForDashboardColumn(data: DashboardColumn): string {
    return this.getIconBaseOnColumnType(data?.columnType.toString(),
      data?.dataModuleType) ?? this.getIconBaseOnDataType(data?.dataType.toString());
  }

  getDataColumnIconForFilter(data: Partial<Filter>): string {
    return this.getIconBaseOnColumnType(data?.columnType.toString(),
      data?.columnDataModuleType) ?? this.getIconBaseOnDataType(data?.columnDataType.toString());
  }

  getDataColumnIconForDescriptor(data: Partial<DescriptorColumn>): string {
    return this.getIconBaseOnColumnType(data?.columnType?.toString(),
      data?.dataModuleType ?? data?.columnDataModuleType) ?? this.getIconBaseOnDataType(data?.columnDataType.toString());
  }

  getDataColumnIconForLegend(data: Partial<LegendColumn>): string {
    return this.getIconBaseOnColumnType(data?.columnType?.toString(),
      data?.dataModuleType ?? data?.columnDataModuleType) ?? this.getIconBaseOnDataType(data?.dataType.toString());
  }

  getDataColumnIconForValue(data: Partial<ValueColumn>): string {

    return this.getIconBaseOnColumnType(data?.columnType?.toString(),
      data?.dataModuleType ?? data?.columnDataModuleType) ?? this.getIconBaseOnDataType(data?.columnDataType.toString());
  }

  getDataColumnIconForSort(data: Partial<ChartInteractiveSort>): string{
    return this.getIconBaseOnColumnType(data?.columnType?.toString(),
      data?.columnDataModuleType) ?? this.getIconBaseOnDataType(data?.columnDataType.toString());
  }
  getIconParameterOnColumnType( columnType: string | undefined) {
    switch (columnType) {
      case 'OriginalColumn':
        return 'parameterColumn';
      case 'Measure':
        return 'parameterMeasure';
      default:
        return undefined;
    }
  }
  getIconBaseOnColumnType(dataType: string | undefined, dataModuleType: DataModuleType) {
    switch (dataType) {
      case 'CalculateColumn':
        return 'customColumn';
      case 'ConditionalColumn':
        return 'customColumn';
      case 'Measure':
        return 'measure';
      default:
        return undefined;
    }
  }
  getIconBaseOnDataType(dataType: string | undefined) {
    switch (dataType) {
      case 'Binary':
        return 'quality';
      case 'Boolean':
        return 'quality';
      case 'Currency':
        return 'quantity';
      case 'Date':
        return 'calendar';
      case 'JalaliDate':
        return 'calendar';
      case 'DecimalNumber':
        return 'quantity';
      case 'Text':
        return 'quality';
      case 'WholeNumber':
        return 'quantity';
    }
  }

  getCalculateColumnMode$(): Observable<CalculateColumnModeType> {
    return this.dashboardState.getCalculateColumnMode$();
  }

  setCalculateColumnMode(mode: CalculateColumnModeType): void {
    this.dashboardState.setCalculateColumnMode(mode);
  }

  getCalculateColumnError$(): Observable<string> {
    return this.dashboardState.getCalculateColumnError$();
  }

  setCalculateColumnError(error: string){
    this.dashboardState.setCalculateColumnError(error);
  }

  getEditCalculateColumn$(): Observable<Partial<CalculateColumnModel>> {
    return this.dashboardState.getEditCalculateColumn$();
  }

  setEditCalculateColumn(editCalculateColumn): void {
    this.dashboardState.setEditCalculateColumn(editCalculateColumn);
  }

  getColumnOriginName(columnId: string, dataSourceId: string, workspaceId: string): Observable<any> {
    return this.dashboardApi.getColumnOriginName(columnId,dataSourceId,workspaceId);
  }

  createCalculateColumn(calculateColumn: Partial<CalculateColumnModel>){
    const _calculateColumn: Partial<CalculateColumnModel> = {
      workspaceId: this.dashboardState.workspaceId,
      dataSourceId: calculateColumn.dataSourceId,
      columnName: calculateColumn.columnName,
      expression: calculateColumn.expression,
      dashboardId: this.dashboardState.dashboardId,
    };
    this.setCalculateColumnSubmit('submitting');
    this.dashboardApi.createCalculateColumn(_calculateColumn).subscribe(
      (res)=> {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.setCalculateColumnSubmit('submitted');
        const dashboardColumn: DashboardColumn = res.data;
        dashboardColumn.isOriginalColumn = false;
        dashboardColumn.hasSort = false;
        dashboardColumn.columnSortType = 'None';
        dashboardColumn.isAscending = false;
        this.dashboardState.addCalculateColumnToDataSource(dashboardColumn, res.data.dataSourceId);
      },
      (err) => {
        this.setCalculateColumnSubmit('submittedWithError');
        this.setCalculateColumnError(err.error.message);
      },
    );
  }

  createMeasure(calculateColumn: Partial<CalculateColumnModel>){
    const _calculateColumn: Partial<CalculateColumnModel> = {
      workspaceId: this.dashboardState.workspaceId,
      dataSourceId: calculateColumn.dataSourceId,
      columnName: calculateColumn.columnName,
      expression: calculateColumn.expression,
      dashboardId: this.dashboardState.dashboardId,
    };
    this.setCalculateColumnSubmit('submitting');
    this.dashboardApi.createMeasure(_calculateColumn).subscribe(
      (res)=> {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.setCalculateColumnSubmit('submitted');
        const dashboardColumn: DashboardColumn = res.data;
        dashboardColumn.isOriginalColumn = false;
        dashboardColumn.hasSort = false;
        dashboardColumn.columnSortType = 'None';
        dashboardColumn.isAscending = false;
        this.dashboardState.addCalculateColumnToDataSource(dashboardColumn, res.data.dataSourceId);
      },
      (err) => {
        this.setCalculateColumnSubmit('submittedWithError');
        this.setCalculateColumnError(err.error.message);
      },
    );
  }

  deleteCalculateColumn(calculateColumn: Partial<CalculateColumnModel>){
    this.dashboardApi.deleteCalculateColumn(calculateColumn.columnId,
      this.dashboardState.workspaceId,
      calculateColumn.dataSourceId, this.dashboardState.dashboardId).subscribe(
      () => {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteDataSourceColumnFromDashboard(calculateColumn.dataSourceId, calculateColumn.columnId);
        this.dashboardState.setSelectedChartMissingFilter(calculateColumn.dataSourceId, calculateColumn.columnId);
        const dashboard = {...this.dashboardState.getDashboardValue()};
        dashboard.charts = this.dashboardState
            .setMissingChartsColumns(dashboard.charts, calculateColumn.dataSourceId, calculateColumn.columnId);
        this.dashboardState.updateDashboard(dashboard);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteMeasure(calculateColumn: Partial<CalculateColumnModel>){
    this.dashboardApi.deleteMeasure(calculateColumn.columnId,
      this.dashboardState.workspaceId,
      calculateColumn.dataSourceId, this.dashboardState.dashboardId).subscribe(
      () => {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.dashboardState.deleteDataSourceColumnFromDashboard(calculateColumn.dataSourceId, calculateColumn.columnId);
        this.dashboardState.setSelectedChartMissingFilter(calculateColumn.dataSourceId, calculateColumn.columnId);
        const dashboard = {...this.dashboardState.getDashboardValue()};
        dashboard.charts = this.dashboardState
            .setMissingChartsColumns(dashboard.charts, calculateColumn.dataSourceId, calculateColumn.columnId);
        this.dashboardState.updateDashboard(dashboard);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateCalculateColumn(calculateColumn: Partial<CalculateColumnModel>){
    this.setCalculateColumnSubmit('submitting');
    calculateColumn.workspaceId = this.dashboardState.workspaceId;
    calculateColumn.dashboardId = this.dashboardState.dashboardId;
    this.dashboardApi.updateCalculateColumn(calculateColumn).subscribe(
      (res)=> {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.reloadAllDashboardCharts();
        this.setCalculateColumnSubmit('submitted');
        const dashboardColumn: DashboardColumn = res.data;
        dashboardColumn.isOriginalColumn = false;
        dashboardColumn.hasSort = false;
        dashboardColumn.columnSortType = 'None';
        dashboardColumn.isAscending = false;
        this.dashboardState.updateCalculateColumnInDataSource(dashboardColumn, res.data.dataSourceId);
      },
      (err)=>{
        this.setCalculateColumnSubmit('submittedWithError');
        this.setCalculateColumnError(err.error.message);
      },
    );
  }

  reloadAllDashboardCharts(isAutoRefresh: boolean = false){
    // this.updateSelectedChart(undefined);
    // this.loadDashboard(this.dashboardState.workspaceId, this.dashboardState.dashboardId);
    this.getDashboardCharts$().pipe(take(1)).subscribe(charts=>{
      if(charts.length > 0) {
        charts.forEach(chart => {
          chart.isLoading = true;
          this.dashboardState.updateOneChart(chart);
          // this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
        });
      }
    });
    const id = this.dashboardState.workspaceId;
    const idd = this.dashboardState.dashboardId;
    this.resetStates()
    // this.dashboardState.resetDashboardState();
    // this.updateSelectedChart(null);
    this.loadDashboard(id, idd,false,isAutoRefresh,null);
    this.loadDataSourcesOfDashboard(id, idd);
    this.loadDashboardUserFilters(id, idd, false);
    this.dashboardSettingSidebarToggleCompact(false);
  }

  updateMeasure(calculateColumn: Partial<CalculateColumnModel>){
    this.setCalculateColumnSubmit('submitting');
    calculateColumn.workspaceId = this.dashboardState.workspaceId;
    calculateColumn.dashboardId = this.dashboardState.dashboardId;
    this.dashboardApi.updateMeasure(calculateColumn).subscribe(
      (res)=> {
        this.toastr.showSuccess();
        this.updateShouldSaveDashboard();
        this.reloadAllDashboardCharts();
        this.setCalculateColumnSubmit('submitted');
        const dashboardColumn: DashboardColumn = res.data;
        dashboardColumn.isOriginalColumn = false;
        dashboardColumn.hasSort = false;
        dashboardColumn.columnSortType = 'None';
        dashboardColumn.isAscending = false;
        this.dashboardState.updateCalculateColumnInDataSource(dashboardColumn, res.data.dataSourceId);
      },
      (err)=>{
        this.setCalculateColumnSubmit('submittedWithError');
        this.setCalculateColumnError(err.error.message);
      },
    );
  }

  loadCalculateColumnExpression(column: Partial<CalculateColumnModel>, dataSourceId: string) {
    this.dashboardApi.getCalculateColumnExpression(column.columnId, this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, dataSourceId).subscribe(
      (res) => {
        column.expression = res.data.expression;
        this.dashboardState.setEditCalculateColumn(column);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getCalculateColumnSubmit$(): Observable<SubmitType> {
    return this.dashboardState.getCalculateColumnSubmit$();
  }

  setCalculateColumnSubmit(submit: SubmitType): void {
    this.dashboardState.setCalculateColumnSubmit(submit);
  }

  updateSliderControllerProperties(chart: Partial<ChartModel>){
    this.dashboardApi
      .updateSliderControllerProperties(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.dashboardState.updateOneChart(chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  uploadImageChart(formFile: FormData, chart: Partial<ChartModel>, uploadType: string): void{
    this.dashboardApi.uploadFile(formFile, this.dashboardState.workspaceId, uploadType).subscribe(
      (res: any) => {
        this.updateShouldSaveDashboard();
        chart.imagePath = res.data.path;
        this.dashboardApi.updateChartImage(chart.imagePath,
          this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart.chartId).subscribe(
          ()=>{
            this.dashboardState.updateOneChart(chart);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
        );
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateChartLinkAddress(chart: Partial<ChartModel>, linkAddress: string): void{
      this.updateShouldSaveDashboard();
      chart.linkAddress = linkAddress;
      this.dashboardApi.updateChartLinkAddress(chart.linkAddress,
        this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart.chartId).subscribe(
        ()=>{
          chart.hasError = false;
          this.dashboardState.updateOneChart(chart);
        },
        (errorRes) => {
          chart.hasError = true;
          chart.errorMessage = 'لینک مورد نظر نامعتبر است.';
          this.dashboardState.updateOneChart(chart);
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  deleteImageChart(path: string, chart: Partial<ChartModel>){
    this.dashboardApi.deleteChartImage(chart.workspaceId, chart.dashboardId, chart.chartId).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        chart.imagePath = null;
        this.dashboardState.updateOneChart(chart);
        // this.dashboardApi.updateChartImage(chart.imagePath,
        //   this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart.chartId).subscribe(
        //   ()=>{
        //
        //   },
        //   (errorRes) => {
        //     this.toastr.showError(errorRes.error.message);
        //   },
        // );
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadMapKeys(chart: Partial<ChartModel>, mapFilePath?: string): void {
    this.dashboardApi.getMapKeys(chart, this.dashboardState.workspaceId, this.dashboardState.dashboardId, mapFilePath).subscribe(
      (res) => {
        this.dashboardState.updateMapKeys(res.data.mapKeys);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getMapKeys$(): Observable<any> {
    return this.dashboardState.getMapKeys$();
  }

  uploadShapeMap(formFile: FormData, chart: Partial<ChartModel>, name: string){
    this.dashboardApi.uploadShapeMap(formFile, chart.workspaceId).subscribe(
      (res) => {
        this.updateShouldSaveDashboard();
        this.dashboardApi.addCustomMapFile(formFile, this.dashboardState.workspaceId, this.dashboardState.dashboardId,
            chart.chartId, res.data.path, name).subscribe(
          ()=>{
            this.loadMapsList(chart);
            this.dashboardState.setSubmitState('notSubmitted');
          },
          (errorRes) => {
            this.dashboardState.addToMapsList(undefined);
            this.toastr.showError(errorRes.error.message);
            this.dashboardState.setSubmitState('notSubmitted');
          },
        );
      },
      (errorRes) => {
        this.dashboardState.addToMapsList(undefined);
        this.toastr.showError(errorRes.error.message);
          this.dashboardState.setSubmitState('notSubmitted');
      },
    );
  }

  deleteCustomMap(customMap: Partial<MapModel>, chart: Partial<ChartModel>): void {
      this.dashboardApi.deleteCustomMap(customMap.mapId,this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, chart.chartId).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              this.dashboardState.deleteMapFromList(customMap, chart);
              this.dashboardState.setSubmitState('submitted');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.dashboardState.setSubmitState('submittedWithError');
          },
      );
  }

  loadMapsList(chart: Partial<ChartModel>){
    this.dashboardApi.loadMapsList(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        chart.chartId).subscribe(
      (mapsList) => {
        this.dashboardState.updateMapsList(mapsList.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getMapsList$(): Observable<Partial<MapModel>[]> {
    return this.dashboardState.getMapsList$();
  }

  updateMapType(chart: Partial<ChartModel>, mapTemp: Partial<MapModel>): void{
    this.dashboardApi.updateMapType(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        chart.chartId, mapTemp).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        chart.mapId = mapTemp.mapId;
        chart.mapFilePath = mapTemp.filePath;
          if (chart.hasError) {
              this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
          } else {
              this.dashboardApi.loadMapFile(chart).subscribe(
                  (mapFile) => {
                      chart.mapTopo = mapFile;
                      chart.hasError = false;
                      this.dashboardState.updateOneChart(chart);
                  });
          }
      },
      (errorRes) => {
        chart.mapTopo = null;
        chart.mapId = null;
        chart.mapFilePath = null;
        this.dashboardState.updateOneChart(chart);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateChartConditionalFormattingActivityStatus(chart: Partial<ChartModel>){
    this.dashboardApi.updateChartConditionalFormattingActivityStatus(this.dashboardState.workspaceId,
        this.dashboardState.dashboardId, chart).subscribe(
      ()=>{
        this.updateShouldSaveDashboard();
        this.loadChartData(chart.workspaceId, chart.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateChartConditionalFormattingSettings(chart: Partial<ChartModel>, valueColumn: Partial<ValueColumn>){
    this.dashboardApi.updateConditionalColorSettings(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
        chart.chartId,valueColumn).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateConditionalColorAdvanceGaugeSettings(chart: Partial<ChartModel>, valueColumn: Partial<ValueColumn>){
    this.dashboardApi.updateConditionalColorAdvanceGaugeSettings(this.dashboardState.workspaceId,
      this.dashboardState.dashboardId, chart.chartId,valueColumn).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  addBookmark(dashboard: Dashboard): void{
    dashboard.isBookmarked = true;
    this.dashboardState.updateDashboard(dashboard);
    this.dashboardApi.addBookmark(dashboard.dashboardId, dashboard.workspaceId).subscribe(
      () =>{
      },
      (errorRes) => {
        dashboard.isBookmarked = false;
        this.dashboardState.updateDashboard(dashboard);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  removeBookmark(dashboard: Dashboard): void{
    dashboard.isBookmarked = false;
    this.dashboardState.updateDashboard(dashboard);
    this.dashboardApi.removeBookmark(dashboard.dashboardId, dashboard.workspaceId).subscribe(
      () => {
      },
      (errorRes) => {
        dashboard.isBookmarked = true;
        this.dashboardState.updateDashboard(dashboard);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  dashboardDuplicate(workspaceId: string, dashboardId: string, dashboardName: string): void {
    const infoToster = this.toastr.showInfo(`در حال کپی داشبورد "${dashboardName}"`, null);
    this.dashboardApi.duplicateDashboard(workspaceId, dashboardId).subscribe(
      (res) => {
        this.toastr.removeToast(infoToster.toastId);
          if (res?.data?.hasError) {
            const dialogRef = this.dialogService.open(MessageDialogComponent, {
            closeOnBackdropClick: false,
            context: {
              message: 'برخی از معیارها دارای خطا هستند و در داشبورد جدید کپی نمی‌شوند.',
              messageDetail: 'نمودارهای وابسته در داشبورد جدید با خطا مواجه خواهند شد.',
              errorsList: res.data.errors,
              errorsListTitle: 'موارد خطا (' + res.data.errors.length + ')',
              icon: 'warning',
              title: 'اخطار',
            },
          });

           dialogRef.onClose.subscribe(() => {
            this.toastr.showSuccess('داشبورد تکرارشده بدون اعمال مجوز داشبورد اصلی با موفقیت ایجاد شد.');
            this.router.navigate(['dashboard', 'view', this.dashboardState.workspaceId, res.data.dashboardId]);
           });
          } else {
          this.toastr.showSuccess('داشبورد تکرارشده بدون اعمال مجوز داشبورد اصلی با موفقیت ایجاد شد.');
          this.router.navigate(['dashboard', 'view', this.dashboardState.workspaceId, res.data.dashboardId]);
          }
      },
      (errorRes) => {
        this.toastr.removeToast(infoToster.toastId);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteDashboard(): void{
    if(this.dashboardState.getIsEditModeValue()) {
        this.dashboardApi.deleteDashboard(this.dashboardState.workspaceId,
            this.dashboardState.getMainDashboardId()).subscribe();
    }
    this.dashboardApi.deleteDashboard(this.dashboardState.workspaceId, this.dashboardState.dashboardId).subscribe(
      () => {
        this.dashboardState.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.dashboardState.setSubmitState('submittedWithError');
      },
    );
  }

  updateChartsHasUserSort(chart: Partial<ChartModel>): void {
      this.dashboardState.updateChartsHasUserSort(chart);
  }

  //user filter
  loadUserFilterDetailsInViewMode(userFilter: Partial<UserFilter>, userFilterType: 'Dashboard' | 'Chart' = undefined): void {
      if(userFilterType === 'Dashboard') {
          this.userFilterService.getDashboardUserFilterDetails(this.dashboardState.workspaceId,
              this.dashboardState.dashboardId,userFilter.userFilterId).subscribe(
              (response) => {
                  this.dashboardState.setUserFilterDetails(response.data);
              },
              (errorRes) => {
                  this.toastr.showError(errorRes.error.message);
              },
          );
      } else if(userFilterType === 'Chart') {
          this.userFilterService.getChartUserFilterDetails(this.dashboardState.workspaceId,
              this.dashboardState.dashboardId,userFilter.userFilterId,
              this.dashboardState.getSelectedChartValue().chartId).subscribe(
              (response) => {
                  this.dashboardState.setUserFilterDetails(response.data);
              },
              (errorRes) => {
                  this.toastr.showError(errorRes.error.message);
              },
          );
      }
  }

  getUserFilterDetails$(): Observable<UserFilterDetails> {
      return this.dashboardState.getUserFilterDetails$();
  }

  getSelectedUsersInUserFilter$(): Observable<Partial<UserInUserFilter>[]> {
      return this.dashboardState.getSelectedUsersInUserFilter$();
  }

  loadDashboardUserFilters(workspaceId: string, dashboardId: string, dashboardUserFilterIsPermitted: boolean): void {
    this.dashboardState.setDashboardUserFilterIsPermitted(dashboardUserFilterIsPermitted);
    if(dashboardUserFilterIsPermitted) {
        this.userFilterService.getDashboardUserFilters(workspaceId,
            dashboardId).subscribe(
            (response) => {
                this.dashboardState.setDashboardUserFilters(response.data);
                this.loadUserFiltersToAddDashboard(workspaceId, dashboardId, response.data);
            },
            (errorRes) => {
                this.toastr.showError(errorRes.error.message);
            },
        );
    }
  }

  getDashboardUserFilterIsPermitted$(): Observable<boolean> {
      return this.dashboardState.getDashboardUserFilterIsPermitted$();
  }

  getDashboardUserFilters$(): Observable<string[]> {
      return this.dashboardState.getDashboardUserFilters$();
  }

  clearUserFilterFromDashboard(){
    this.dashboardState.clearUserFilterFromDashboard();
  }

  getChartsUserFilters$(): Observable<string[][]> {
    return this.dashboardState.getChartsUserFilters$();
  }

  loadChartUserFilters(dashboardUserFilterIsPermitted: boolean): void {
      if(dashboardUserFilterIsPermitted) {
          const selectedChart = this.dashboardState.getSelectedChartValue() ?? this.dashboardState.getNewChartValue();
          if(selectedChart.chartType !== 'ImageChart' && selectedChart.chartType !== 'TextChart' &&
            selectedChart.chartType !== 'IframeChart') {
              this.userFilterService.getChartUserFilters(this.dashboardState.workspaceId,
                  this.dashboardState.dashboardId, selectedChart.chartId).subscribe(
                  (response) => {
                      this.dashboardState.setChartUserFilters(response.data, selectedChart.chartIndexInDashboard);
                      this.loadUserFiltersToAddChart(response.data, selectedChart);
                  },
                  (errorRes) => {
                      this.toastr.showError(errorRes.error.message);
                  },
              );
          }
      }
  }

  updateChartUserFilters(userFilters: string[], chartIndex: number): void {
      this.dashboardState.updateChartUserFilters(userFilters, chartIndex);
  }

  getSelectedChartUserFilters$(): Observable<string[]> {
      return this.dashboardState.getSelectedChartUserFilters$();
  }

  addUserFilterToDashboard(userFilter: Partial<UserFilter>): void {
      this.userFilterService.addUserFilterToDashboard(this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, userFilter.userFilterId).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              this.dashboardState.addUserFilterToDashboard(userFilter);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  removeUserFilterFromDashboard(userFilterId: string): void {
      this.userFilterService.removeUserFilterFromDashboard(this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, userFilterId).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              this.dashboardState.removeUserFilterFromDashboard(userFilterId);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  loadUserFiltersToAddDashboard(workspaceId: string, dashboardId: string,
                                selectedUserFilters: Partial<UserFilter>[]): void {
      this.userFilterService.getUserFiltersToAddDashboard(workspaceId, dashboardId).subscribe(
          (response) => {
              this.dashboardState.setUserFiltersListAddToDashboard(response.data, selectedUserFilters);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getUserFiltersListAddToDashboard$(): Observable<Partial<UserFilter>[]> {
      return this.dashboardState.getUserFiltersListAddToDashboard$();
  }

  loadUserFiltersToAddChart(selectedUserFilters: Partial<UserFilter>[], chart: Partial<ChartModel>): void {
      this.userFilterService.getUserFiltersToAddChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
          chart.chartId).subscribe(
          (response) => {
              this.dashboardState.setUserFiltersListAddToChart(chart, response.data, selectedUserFilters);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getSelectedChartUserFiltersListAddToChart$(): Observable<Partial<UserFilter>[]> {
    return this.dashboardState.getSelectedChartUserFiltersListAddToChart$();
  }

  addUserFilterToChart(userFilter: Partial<UserFilter>, chart: Partial<ChartModel>): void {
      this.userFilterService.addUserFilterToChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
          userFilter.userFilterId, chart.chartId).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              this.dashboardState.addUserFilterToChart(userFilter, chart);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  removeUserFilterFromChart(userFilterId: string, chart: Partial<ChartModel>): void {
      this.userFilterService.removeUserFilterFromChart(this.dashboardState.workspaceId, this.dashboardState.dashboardId,
          userFilterId, chart.chartId).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              this.dashboardState.removeUserFilterFromChart(userFilterId, chart);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  resetUserFilterDetails(): void {
      this.dashboardState.setUserFilterDetails(undefined);
  }

  resetFactoryChartDesignSettings(chart: Partial<ChartModel>): void {
      chart = chart.isController ? this.chartFactoryService.resetFactoryControllerDesignSettings(chart) :
          this.chartFactoryService.resetFactoryChartDesignSettings(chart);
      this.dashboardApi.updateChartDesign(this.dashboardState.workspaceId, this.dashboardState.dashboardId,chart).subscribe(
          () => {
              this.updateShouldSaveDashboard();
              this.setChartDesign(chart);
          },
          (errorRes) => {
              this.dashboardState.rollBackChartDesignSetting();
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  convertChartType(chart: Partial<ChartModel>, destinationChartType: ChartType ): void{
    chart.isLoading = true;
    const index = chart.chartIndexInDashboard;
    const controllerValues = chart.controllersValues;
    const updatedChartDesignSettings = this.chartFactoryService.convertChartDesignSettings(chart.chartType,
      destinationChartType,chart.chartDesignSettings);
    this.dashboardApi.convertChartType(updatedChartDesignSettings, chart.workspaceId, chart.dashboardId, chart.chartId,
      destinationChartType).subscribe(
      () => {
        this.updateShouldSaveDashboard();
        this.loadConvertedChart(chart, index, controllerValues);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadConvertedChart(convertedChart: Partial<ChartModel>, indexInDashboard: number,
                     controllerValues: Partial<ControllerValue>[]): void {
    this.dashboardApi.getChart(convertedChart.workspaceId, convertedChart.dashboardId, convertedChart.chartId).subscribe(
      (response) => {
        let chart = response.data;
        chart.chartIndexInDashboard = indexInDashboard;
        chart.controllersValues = controllerValues;
        chart.numberDisplayType = convertedChart.numberDisplayType;
        chart.chartDesignSettings = this.jsonAdapterService.jsonify<ChartDesignSettings>(chart.chartDesignSettings);
        chart = this.chartPermissionService.addPermissionToEntity(chart, chart.permissions,
          this.userService.getUserMinInfoValue(), false);
        if(((chart.chartDataSettings.descriptorColumns.length + chart.chartDataSettings.legendColumns.length > 0
              || chart.chartType === 'CardChart' || chart.chartType === 'AdvancedCardChart' || chart.chartType === 'TableChart' ||
              chart.chartType === 'GaugeChart' || chart.chartType === 'AdvancedGaugeChart') &&
            chart.chartType !== 'SankeyChart' && chart.chartType !== 'KPIChart' &&
            chart.chartType !== 'CollapsableNetworkChart' &&
            chart.chartDataSettings.valueColumns.length + chart.chartDataSettings.secondValueColumns.length > 0) ||
          (chart.isController && chart.chartDataSettings.descriptorColumns.length > 0) ||
          ((chart.chartType === 'SankeyChart' || chart.chartType === 'CollapsableNetworkChart')
            && chart.chartDataSettings.descriptorColumns.length > 0 &&
            chart.chartDataSettings.legendColumns.length > 0) ||
          (chart.chartType === 'KPIChart' && chart.chartDataSettings.descriptorColumns.length > 0 &&
            chart.chartDataSettings.valueColumns.length > 0)) {
          this.loadChartData(chart.workspaceId, chart.dashboardId, chart, true);
        }else{
          this.setSettingSidebarId(chart.chartType);
          this.dashboardState.updateOneChart(chart);
          this.setChartDesign(chart);
          this.updateRollbackChartSetting(chart);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  setExportChartDataDetail(data: { chartId: string; exportType: ExportChartDataType }): void {
    this.dashboardState.setExportChartDataDetail(data);
  }

  getExportChartDataDetail$(): Observable<{ chartId: string; exportType: ExportChartDataType }> {
    return this.dashboardState.getExportChartDataDetail$();
  }

  sidebarMenuChangeRoute(link: string): void {
      this.sidebarService.changeRoute(link);
  }

  loadMinMaxColumnData(datasourceId: string, columnId: string, isApplyMainFilter: boolean): void {
      this.dashboardApi.getMinMaxColumnData(this.dashboardState.workspaceId,
          this.dashboardState.dashboardId, datasourceId, columnId, isApplyMainFilter).subscribe(
          (res) => {
              this.dashboardState.setMinMaxColumnData(res.data.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getMinMaxColumnData$(): Observable<string []> {
      return this.dashboardState.getMinMaxColumnData$();
  }

  updateCompactedDashboardListStatus(compacted: boolean): void {
      this.dashboardState.updateCompactedDashboardListStatus(compacted);
  }

  getCompactedDashboardListStatus$(): Observable<boolean> {
      return this.dashboardState.getCompactedDashboardListStatus$();
  }

  updateDashboardListResizeStatus(compacted: boolean): void {
      this.dashboardState.updateDashboardListResizeStatus(compacted);
  }

  getDashboardListResizeStatus$(): Observable<boolean> {
      return this.dashboardState.getDashboardListResizeStatus$();
  }
  updateDashboardPinStatus(dashboardId: string, sortOrder: SortOrder): void {
      this.dashboardApi.updateDashboardPinStatus(dashboardId).subscribe(
          () => {
              this.loadAssetsOfWorkspace(this.dashboardState.workspaceId, this.dashboardState.dashboardId, sortOrder);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  updateDashboardGroupPinStatus(dashboardGroupId: string, sortOrder: SortOrder): void {
      this.dashboardApi.updateDashboardGroupPinStatus(dashboardGroupId).subscribe(
          () => {
              this.loadAssetsOfWorkspace(this.dashboardState.workspaceId, this.dashboardState.dashboardId, sortOrder);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  updateSettingsBeforeSavingDashboard(): void {
      this.updateChartDesign();
      this.updateChartAnnotations();
      this.updateDashboardChartGrids();
  }

  /**
   * call createDescriptorColor from dashboard api to get colors for bar chart
   */
  getBarChartDescriptorColors(
      column: Partial<DescriptorColumn | ColumnData>,
      workSpaceId: string,
      dashboardId: string,
      chartId: string) {
    return this.dashboardApi.createDescriptorColumnColors(
      column,
      workSpaceId,
      dashboardId,
      chartId,
    );
  }


  /**
   *
   * @param inputColumnsData
   * @param chart current chart
   * make a call to api and get descriptor column  colors then update accordingly,
   * this function is used before update chart data for barchart and ... so the descriptor colors update happens smoothly
   */
  updateBarChartDescriptorColors(
      inputColumnsData: ColumnData[],
      chart: Partial<ChartModel>){

    this.getBarChartDescriptorColors(
        chart.chartDataSettings.descriptorColumns[0],
        this.dashboardState.workspaceId,
        this.dashboardState.dashboardId,
        chart.chartId,
    ).subscribe(
      res=>{
        if(inputColumnsData?.length){
          const currentDescriptorColumnIndex = inputColumnsData.findIndex( item => item.chartColumnType === 'Descriptor');
          if(currentDescriptorColumnIndex !== -1){
            inputColumnsData[currentDescriptorColumnIndex]['colors'] = res.data.colors;
            this.dashboardState.updateChartData(inputColumnsData , chart.chartId);
          }
        }
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
    );
  }

  /**
   * this method helps with transferring an array holding lines in combo charts
   */
  setLinesArray(linesArray: []){
    const lines = linesArray;
    return lines;
  }

  private handleChartsIssue(res: any, chart: Partial<ChartModel>){
    if(chart.chartType === 'AdvancedGaugeChart' && res.data && res.data.columnsData){
      const valueColumnIndex = res.data.columnsData.findIndex(column=>column.chartColumnType === 'Value');
      const chartHasTargetConfig = (chart?.chartDataSettings.secondValueColumns.length > 0);
      const targetValueIndex = res.data.columnsData.findIndex(c => c.chartColumnType === 'SecondValue');
      const hasNoIndicatorValue = (res.data.columnsData[valueColumnIndex] &&
        res.data.columnsData[valueColumnIndex].data && !res.data.columnsData[valueColumnIndex].data.length);
      // i omit hasNoIndicatorValue condition because it is not a problem in the advanced gauge chart i set another error page for this condition while the data is exist but no value it has i shouldnt set the haserror true i work with noDataExist parameter.
      // if(valueColumnIndex === -1 || (chartHasTargetConfig && targetValueIndex === -1) || hasNoIndicatorValue){
      if(valueColumnIndex === -1 || (chartHasTargetConfig && targetValueIndex === -1)){
        this.dashboardState.setChartError(chart.chartId, 'تنظیمات نمودار به درستی انجام نشده است.');
      }
    }else if(chart.chartType === 'CardChart' || chart.chartType === 'AdvancedCardChart'){
      if(!res.data || !res?.data?.columnsData){
        chart.chartDesignSettings.chart.customBackgroundColor = undefined;
      }else if(res.data && res.data.columnsData.length){
        const conditionalColoringFieldIndex =
          res.data.columnsData?.findIndex(column => column.isConditionalFormattingColumn);
        if(conditionalColoringFieldIndex === -1){
          chart.chartDesignSettings.chart.customBackgroundColor = undefined;
        }
      }
    }
  }

  changeSortColumnPriority(sortColumn: Partial<ChartInteractiveSort>, newPriority: number, chart: Partial<ChartModel>,
                          previousIndex: number, currentIndex: number): void {
    this.dashboardApi.updateChartSort(
      this.dashboardState.workspaceId,
      this.dashboardState.dashboardId,
      chart.chartId,
      {
        ...sortColumn,
        priority: newPriority
      }
    ).subscribe(
      (res: any) => {
        const status: SortError = res?.data?.priorityStatus;
        switch (status) {
          case SortError.NumberOfColumnsNotEqual:
            this.toastr.showInfo('تعداد ستون‌های مرتب‌سازی با ستون‌های استفاده‌شده در نمودار برابر نیست.');
            break;
          case SortError.PrioritiesNotEqual:
            this.toastr.showInfo('مرتب‌سازی غیرفعال شد: یکی از ستون‌های اصلی حذف یا جابجا شده است.');
            break;
        }
        this.updateShouldSaveDashboard();
        this.dashboardState.changeSortColumnPriority(sortColumn, newPriority, currentIndex, previousIndex, chart.chartId);
        this.loadChartData(this.dashboardState.workspaceId, this.dashboardState.dashboardId, chart);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadDefaultDataSource(workspaceId: string, dashboardId: string): void {
    this.dashboardApi.getDefaultDataSource(workspaceId, dashboardId).subscribe(
      (res) => {
        this.dashboardState.setDefaultDataSource(res.data.updateDateTimeDataSource);
        this.dashboardState.setDefaultDataSourceDate(res.data.updateDateTimeDataSource.lastModifiedDataSourceTickTime);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      }
    );
  }

  updateDefaultDataSource(workspaceId: string, dashboardId: string, defaultDataSourceId: string, dashboard: Dashboard): void {
    this.dashboardApi.updateDefaultDataSource(workspaceId, dashboardId, defaultDataSourceId).subscribe(
      (res) => {
        this.toastr.showSuccess();
        // this.loadDefaultDataSource(workspaceId, dashboardId);
        // this.dashboardState.setDefaultDataSource(res.data);
        this.dashboardState.setDefaultDataSourceDate(res.data.lastModifiedDataSourceTickTime);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      }
    );
  }

  getDefaultDataSource$(): Observable<Partial<Datasource>> {
    return this.dashboardState.getDefaultDataSource$();
  }
  getDefaultDataSourceDate$(): Observable<number> {
    return this.dashboardState.getDefaultDataSourceDate$();
  }

  getDefaultDataSourceValue(): Partial<Datasource> {
    return this.dashboardState.getDefaultDataSourceValue();
  }
  getDashboardInfo(dashboardId: string, workspaceId: string): void {
    this.dashboardState.updateDashboardInfo({
      dashboardId,
      workspaceId,
      isLoading: true
    } as DashboardInfoDto);

    this.dashboardApi.getDashboardInfo(dashboardId, workspaceId).subscribe(
      (res) => {
        this.dashboardState.updateDashboardInfo({
          ...res.data,
          isLoading: false
        });
      },
      (errorRes) => {
        this.dashboardState.updateDashboardInfo({
          dashboardId,
          workspaceId,
          isLoading: false
        } as DashboardInfoDto);
        this.toastr.showError(errorRes.error.message);
      }
    );
  }
  getDashboardInfo$(): Observable<DashboardInfoDto> {
    return this.dashboardState.getDashboardInfo$();
  }

  saveDashboardInfo(
    dashboardId: string,
    workspaceId: string,
    dashboardAudience: number,
    organizationName: string,
    dashboardDescription: string,
    businessDescription: string
  ): void {
    // شروع لودینگ
    this.dashboardState.updateDashboardInfo({
      dashboardId,
      workspaceId,
      isLoading: true
    } as DashboardInfoDto);

    this.dashboardApi
      .saveDashboardInfo(
        dashboardId,
        workspaceId,
        dashboardAudience,
        organizationName,
        dashboardDescription,
        businessDescription
      )
      .subscribe(
        () => {
          this.toastr.showSuccess();
          this.dashboardState.updateDashboardInfo({
            dashboardId,
            workspaceId,
            isLoading: false
          } as DashboardInfoDto);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
          this.dashboardState.updateDashboardInfo({
            dashboardId,
            workspaceId,
            isLoading: false
          } as DashboardInfoDto);
        }
      );
  }

  loadChartInsight(
    workspaceId: string,
    dashboardId: string,
    chart: Partial<ChartModel>,
    name: string,
    chartType: string,
    insightTone: string,
    insightSize: string
  ): void {
    this.dashboardState.updateChartInsight({
      chartId: chart.chartId,
      isGenerating: true,
      text: null,
      statusCode: null,
    });

    let interactionValues = this.dashboardState.getValidInteractionValues(chart, undefined);
    const drillingFilterValues = this.dashboardState.getValidDrillingFilterValues(chart.chartIndexInDashboard);

    if (!chart?.paginationValues && chart.isController && chart.chartType === 'DropdownController') {
      chart.paginationValues = {
        searchKey: null,
        needNewQuery: true,
        values: null,
      };
    }

    if (chart?.chartType === 'TableChart' && chart?.chartDesignSettings?.chart?.isPaginated) {
      chart.paginationValues = {
        rowNumber: chart?.chartDesignSettings?.chart?.linesInPage,
      };
    }

    this.dashboardState.getDrillThroughChartFilters$().pipe(take(1)).subscribe(chartFilters => {
      this.dashboardApi
        .getChartInsight(
          workspaceId,
          dashboardId,
          chart,
          name,
          chartType,
          insightTone,
          insightSize,
          chart.controllersValues,
          interactionValues,
          drillingFilterValues,
          chartFilters
        )
        .subscribe(
          (res) => {
            this.dashboardState.updateChartInsight({
              chartId: chart.chartId,
              isGenerating: false,
              text: res.data,
              statusCode: null,
            });
          },
          (errorRes) => {
            const statusCode = errorRes?.error ? errorRes?.error.statusCode : errorRes?.status;
            this.toastr.showError('دریافت بینش هوشمند با خطا مواجه شد. لطفا دوباره تلاش کنید.');
            this.dashboardState.updateChartInsight({
              chartId: chart.chartId,
              isGenerating: false,
              text: null,
              statusCode,
            });
          }
        );
    });
  }

  getChartInsight$(): Observable<any> {
    return this.dashboardState.getChartInsight$();
  }

  getChartInsightValue(): any {
    return this.dashboardState.getChartInsightValue();
  }

  loadDashboardHistory(workspaceId: string, dashboardId: string): void {
    this.dashboardState.setDashboardHistoryLoading(true);

    this.dashboardApi.getDashboardHistory(workspaceId, dashboardId).subscribe(
      (res) => {
        this.dashboardState.setDashboardHistoryCurrent(res.data.dashboard);
        this.dashboardState.setDashboardHistoryList(res.data.histories);
      },
      (error) => {
        this.toastr.showError(error.message ||'خطا در دریافت تاریخچه داشبورد');
      },
      () => {
        this.dashboardState.setDashboardHistoryLoading(false);
      }
    );
  }

  loadDashboardRecovery(workspaceId: string, dashboardId: string): void {
    this.dashboardState.setDashboardRecoveryLoading(true);
    this.dashboardState.setDashboardRecoverySuccess(false);

    this.dashboardApi.recoveryDashboard(workspaceId, dashboardId).subscribe(
      (res) => {
        this.dashboardState.setDashboardRecoveryLoading(false);
        this.dashboardState.setDashboardRecoverySuccess(true);
        this.dashboardState.setDashboardRecoveryCurrent(res.data);
        this.toastr.showSuccess();
        this.router.navigate(['dashboard', 'view', res.data.workspaceId, res.data.dashboardId],
        {
          queryParams: {},
          queryParamsHandling: '',
          replaceUrl: true
        });

      },
      (error) => {
        this.dashboardState.setDashboardRecoveryLoading(false);
        this.dashboardState.setDashboardRecoverySuccess(false);
        this.toastr.showError(error.message || 'خطا در بازیابی داشبورد');
      }
    );
  }

changePinHistoryDashboard(workspaceId: string, dashboardId: string, currentDashboardId: string): void {
  this.dashboardState.setDashboardPinLoading(true);

  this.dashboardApi.changePinHistoryDashboard(workspaceId, dashboardId).subscribe(
    (res) => {
      this.dashboardState.setDashboardPinLoading(false);
      this.dashboardState.setDashboardPinCurrent(res.data);
      this.loadDashboardHistory(workspaceId,currentDashboardId)
    },
    (error) => {
      this.dashboardState.setDashboardPinLoading(false);
      this.toastr.showError(error.message || 'خطا در پین/آن‌پین داشبورد');
    }
  );
 }
  setToggleDashboardInHistoryMode(toggle : boolean){
    this.dashboardState.toggleDashboardInHistoryMode(toggle)
  }
  setDashboardDuplicatedName(value : boolean){
    this.dashboardState.setDashboardNameDuplicated(value)
}
loadDefaultDatasourceChart(
  workspaceId: string,
  dashboardId: string,
  chartId: string
): void {
  // Set loading state
  this.dashboardState.setChartDatasourceState(chartId, { loading: true });

  const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

  this.dashboardApi.getDefaultDataSourceChart({
    workspaceId,
    dashboardId,
    chartId
  }).subscribe(
    res => {
      const dataSources = res.data.dataSources || [];
      let defaultId = res.data.defaultDataSourceId;

      // If defaultId is equal to an empty GUID, use the first item
      if (defaultId === EMPTY_GUID && dataSources.length > 0) {
        defaultId = dataSources[0].dataSourceId;
      }

      // Set state
      this.dashboardState.setChartDatasourceState(chartId, {
        list: dataSources,
        selectedId: defaultId,
        hasDefault: true,
        loading: false,
        tickTime: res.data?.lastModifiedDataSourceTickTime ?? null
      });

      if (dataSources.length > 0) {
        this.setDefaultDatasourceChart(
          workspaceId,
          dashboardId,
          chartId,
          defaultId,
           true
        );
      } else {
        this.dashboardState.setChartDatasourceState(chartId, { loading: false });
      }
    },
    err => {
      this.toastr.showError(err.message);
      this.dashboardState.setChartDatasourceState(chartId, { loading: false });
    }
  );
}

setDefaultDatasourceChart(
  workspaceId: string,
  dashboardId: string,
  chartId: string,
  dataSourceId: string | null,
  hasDefault: boolean
): void {
  const chart = this.dashboardState.getChartValue(chartId);
  if (!chart) {
    return;
  }

  this.dashboardApi.setDefaultDataSourceChart({
    workspaceId,
    dashboardId,
    chartId,
    dataSourceId,
    hasDefaultDataSource: hasDefault
  }).subscribe(
    res => {
      // Update state
      this.dashboardState.setChartDatasourceState(chartId, {
        selectedId: res.data?.dataSourceId ?? null,
        hasDefault: hasDefault,
        tickTime: res.data?.lastModifiedDataSourceTickTime ?? null
      });

      // Update chart model
      chart.hasDefaultDatasource = hasDefault;
      chart.lastModifiedDataSourceTickTime = res.data?.lastModifiedDataSourceTickTime ?? null;
      if (!hasDefault) {
        chart.chartDesignSettings.chart.datasourceDateToggle = false;
      }

      this.dashboardState.updateOneChart(chart);
    },
    err => {
      this.toastr.showError(err.message);
    }
  );
}

setNameSubmitState(value : SubmitType){
  this.dashboardState.setNameSubmitState(value)
}

  updatePainFormat(data: any){
    this.dashboardState.paintFormatState.set(data)
  }

  updateAdvancedCardGridState(data: any){
    this.dashboardState.advancedCardGridState.set(data)
  }


  //import export dashboard
  exportDashboard(workspaceId: string, dashboardId: string): void {
    this.dashboardState.exportDashboardLoading.set(true);

    this.dashboardApi.exportDashboard(workspaceId, dashboardId).subscribe(
      res => {
        this.dashboardState.exportDashboardLoading.set(false);

        const fullPath = res.data; // مسیر فایل از API
        const rawFileName = fullPath.split('/').pop() || '';

        const cleanFileName = rawFileName
          .replace(/^C__TartFiles_ExportDashboardFiles_/, '')
          .replace(/^ExportDashboardFiles_?/, '');

        fetch(this.baseAddress + fullPath)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = cleanFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(() => {
            this.dialogService.open(ExportWarningModalComponent, {
              closeOnBackdropClick: false,
              context: {
                messageOne: 'این داشبورد به دلیل وجود خطا در منبع داده یا ساختار کارت‌ها قابل خروجی گرفتن نیست.',
                messageTwo: 'برای ادامه، ابتدا خطاهای داشبورد را برطرف کنید.'
              },
            });
          });
      },
      () => {
        this.dashboardState.exportDashboardLoading.set(false);
        this.dialogService.open(ExportWarningModalComponent, {
          closeOnBackdropClick: false,
          context: {
            messageOne: 'این داشبورد به دلیل وجود خطا در منبع داده یا ساختار کارت‌ها قابل خروجی گرفتن نیست.',
            messageTwo: 'برای ادامه، ابتدا خطاهای داشبورد را برطرف کنید.'
          },
        });
      }
    );
  }

  startImportWithDatasource(workspaceId: string, file: File): void {
    this.dashboardState.importLoading.set(true);
      this.dashboardState.resetImportState();

    this.dashboardApi.checkImportDashboard(workspaceId, file, false).subscribe(
      res => {
        const data = res.data;

        const datasources = data.dataSources.map(ds => ({
          sourceDatasourceId: ds.sourceDataSourceId,
          name: ds.name,
          status: ds.status,
          errors: ds.errors,
          selectedDatasourceId: ds.selectedDataSourceId ?? null,
          selectedDataSourceName: ds.selectedDataSourceName ?? null
        }));
          this.dashboardState.setImportInitialData(
          data.importDahboardId,
          datasources
        );

        this.dashboardState.moveToFirstUnresolvedStep();

        this.dashboardState.importLoading.set(false);

        this.importDialogRef = this.dialogService.open(ImportDashboardDatasourcesModalComponent, {
          closeOnBackdropClick: false,
          context: { workspaceId },
        });
      },
      err => {
        this.dashboardState.importLoading.set(false);
        this.toastr.showError(err.message);
      }
    );
  }

  setSelectImportDatasource(id: string, name: string): void {
    this.dashboardState.setImportSelectedDatasource(id, name);
  }
  checkImportDatasource(
    workspaceId: string,
    selectedDatasourceId: string,
    selectedDataSourceName: string
  ): void {
    const current = this.dashboardState.importCurrentDatasource();
    const importDashboardId = this.dashboardState.importDashboardId();

    if (!current) return;

    this.dashboardState.importLoading.set(true);

    this.dashboardApi.checkDatasourceImportDashboard(
      workspaceId,
      importDashboardId!,
      current.sourceDatasourceId,
      selectedDatasourceId
    ).subscribe(res => {
      this.dashboardState.importLoading.set(false);

      const isSuccess = res.data.status;
      const errors = res.data.errors ?? [];

      this.dashboardState.setImportDatasourceResult(
        isSuccess,
        errors,
        selectedDatasourceId,
        selectedDataSourceName
      );

      if (isSuccess) {
        this.dashboardState.nextImportStep();
      }
    });

      err => {
        this.dashboardState.importLoading.set(false);

        this.dashboardState.setImportDatasourceResult(
          false,
          [err.message],
          selectedDatasourceId,
          selectedDataSourceName
        );
      }

  }

  backImportStep() {
    const index = this.dashboardState.importStepIndex();
    if (index <= 0) return;

    this.dashboardState.importStepIndex.set(index - 1);
  }




  finalizeImportDashboard(workspaceId: string): void {
    this.dashboardState.importFinalizeLoading.set(true);

    this.dashboardApi.importDashboard(
      workspaceId,
      this.dashboardState.importDashboardId()!,
      false
    ).subscribe(
      res => {
        this.dashboardState.importFinalizeLoading.set(false);
          if (this.importDialogRef) {
          this.importDialogRef.close();
          this.importDialogRef = null;
        }

        this.dashboardState.resetImportState();
        this.router.navigate(['dashboard', 'view', workspaceId, res.data.dashboardId]);
      },
      err => {
        this.dashboardState.importFinalizeLoading.set(false);
        this.toastr.showError(err.message);
      }
    );
  }

  importWithoutDatasource(workspaceId: string, file: File): void {
    this.dashboardState.importFinalizeLoading.set(true);

    this.dashboardApi.checkImportDashboard(workspaceId, file, true).subscribe(
      res => {
        const importDashboardId = res.data.importDahboardId;

        this.dashboardApi.importDashboard(
          workspaceId,
          importDashboardId,
          true
        ).subscribe(
          importRes => {
            this.dashboardState.importFinalizeLoading.set(false);
            this.router.navigate(['dashboard', 'view', workspaceId, importRes.data.dashboardId]);
          },
          err => {
            this.dashboardState.importFinalizeLoading.set(false);
            this.toastr.showError(err.message);
          }
        );
      },
      err => {
        this.dashboardState.importFinalizeLoading.set(false);
        this.toastr.showError(err.message);
      }
    );
  }

  changeImportDatasourceSelection(id: string, name: string): void {
    this.dashboardState.resetCurrentImportDatasourceSelection(id, name);
  }
}


